import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {

  CalendarDays,

  ChevronLeft,

  ChevronRight,

  Clock3,
  Download,

  MoreHorizontal,

  MapPin,

  Rows3,

  Plus,

  Search,
  Stethoscope,

  UserPlus,

  Video,
  X,

  Phone,

  Mail,
  PanelLeft,
  Printer,

} from 'lucide-react';

import { cn } from '../utils/cn';

import { useAuth0 } from '@auth0/auth0-react';

import { API_BASE, AUTH_AUDIENCE } from '../config/env';

import { useNavigate, useSearchParams } from 'react-router-dom';

import AppointmentModal from '../components/AppointmentModal';
import { useI18n } from '../i18n/LanguageContext';
import { useTreatmentCatalogConfig } from '../lib/treatmentCatalog';

import { TelemedVisitStatus } from '../types/telemed';

import { useTelemedApi } from '../lib/telemed';



type ViewMode = 'day' | 'week' | 'month';

type DisplayMode = 'calendar' | 'table' | 'resource';

type CalendarActivity = { id: string; title: string; type: string; date: string; owner?: string; status?: string };
type AppointmentIconLegendItem = { id: string; label: string; color: string; description?: string; enabled?: boolean };
type CalendarPasscodeRules = { minLength: number; requireNumbers: boolean; lockOnIdle: boolean; idleTimeoutMinutes: number };
type CalendarConfiguration = { defaultDisplayMode: DisplayMode; visibleHoursStart: number; visibleHoursEnd: number; showProviderAvatars: boolean; showLocationFilter: boolean };
type AppointmentSettingsConfig = { requireService: boolean; requireRoom: boolean; allowClientCardAccess: boolean; defaultStatus: string; bookingRuleSummary: string };
type ShiftNote = { id: string; date: string; providerId?: string | null; providerName?: string; note: string };
type DayNote = { id: string; date: string; note: string };
type CalendarPreset = { id: string; name: string; view: ViewMode; displayMode?: DisplayMode; filterStatus?: string; providerIds?: string[]; locationIds?: string[] };
type ExternalCalendarSync = { id: string; provider: string; enabled?: boolean; mode?: 'one-way' | 'two-way'; accountLabel?: string; lastSyncedAt?: string | null };
type ClinicListSettings = { enabled: boolean; includeStatus: boolean; includeLocation: boolean; includeService: boolean; defaultOpen: boolean };
type ClinicListRow = { time: string; patient: string; provider: string; service: string; status: string; location: string };
type CalendarBlockout = {
  id: string;
  seriesId?: string;
  title: string;
  startTime: string;
  endTime: string;
  providerIds: string[];
  reason?: string;
  repeatFrequency?: 'none' | 'daily' | 'weekly' | 'monthly';
  repeatCount?: number;
  createdAt?: string;
  updatedAt?: string;
};

type PendingBlockout = {
  id?: string;
  seriesId?: string;
  title: string;
  start: string;
  durationMinutes: number;
  providerIds: string[];
  reason: string;
  repeatEnabled: boolean;
  repeatFrequency: 'daily' | 'weekly' | 'monthly';
  repeatCount: number;
};

const defaultAppointmentIconLegend: AppointmentIconLegendItem[] = [
  { id: 'icon-arrived', label: 'Checked in or arrived', color: '#10b981', enabled: true },
  { id: 'icon-telemed', label: 'Telemedicine appointment', color: '#0ea5e9', enabled: true },
  { id: 'icon-pending', label: 'Forms or care tasks pending', color: '#f59e0b', enabled: true },
  { id: 'icon-risk', label: 'No-show risk or failed reminders', color: '#ef4444', enabled: true },
  { id: 'icon-complete', label: 'Cancelled or completed appointment', color: '#94a3b8', enabled: true },
];



type Appointment = {

  id: string;

  patientId: string;
  encounterId?: string | null;

  patient: string;
  patientAvatarUrl?: string | null;

  provider: string;

  providerId: string;
  providerAvatarUrl?: string | null;

  status: AppointmentStatus;

  appointmentType: AppointmentTypeOption;

  billingService?: {
    id: string;
    name: string;
    category?: string | null;
    price: number;
  } | null;

  start: string; // ISO string

  end: string; // ISO string

  title?: string | null;

  isTelemed?: boolean;

  visitStatus?: TelemedVisitStatus | null;

  joinUrl?: string | null;

  hostToken?: string | null;

  guestToken?: string | null;

  roomName?: string | null;
  roomResourceId?: string | null;
  assetResourceIds?: string[];
  additionalStaffIds?: string[];
  locationId?: string | null;
  location?: { id: string; name: string; isPrimary?: boolean } | null;
  medicalForms?: AppointmentMedicalForm[];
  source?: 'STAFF' | 'PORTAL';
  confirmedAt?: string | null;
  checkedInAt?: string | null;
  arrivedAt?: string | null;
  patientInstructionsAckAt?: string | null;
  noShowRiskScore?: number | null;
  reminderSummary?: { pending: number; sent: number; failed: number };
  careTaskSummary?: { pending: number; completed: number };
  emailReminderEnabled?: boolean;
  smsReminderEnabled?: boolean;
  reminderMinutesBefore?: number | null;

};



type AppointmentStatus = 'SCHEDULED' | 'PORTAL_BOOKED' | 'CHECKED_IN' | 'COMPLETED' | 'CANCELLED' | 'NO_SHOW';

type AppointmentTypeOption = {

  id: string;

  name: string;

  color: string;

  durationMinutes: number;

  isTelemed?: boolean;

  serviceDocumentation?: Record<string, unknown>;

  serviceVariants?: Array<{
    id: string;
    code: string;
    name: string;
    category?: string | null;
    price: number;
    active: boolean;
  }>;

};

function virtualMetaForAppointmentType(type?: AppointmentTypeOption | null) {
  const docs = type?.serviceDocumentation && typeof type.serviceDocumentation === 'object' ? type.serviceDocumentation as Record<string, any> : {};
  const meta = docs.serviceMeta && typeof docs.serviceMeta === 'object' ? docs.serviceMeta as Record<string, any> : {};
  const virtualDeliveryMode = meta.virtualDeliveryMode === 'hybrid' || meta.virtualDeliveryMode === 'virtual'
    ? meta.virtualDeliveryMode
    : (type?.isTelemed ? 'virtual' : 'in_person');
  return {
    virtualDeliveryMode,
    groupVideoEnabled: Boolean(meta.groupVideoEnabled),
    maxVideoParticipants: Number(meta.maxVideoParticipants || 2),
    autoCreateVideoSession: Boolean(meta.autoCreateVideoSession ?? true),
    videoProviderOverride: String(meta.videoProviderOverride || ''),
  };
}

function classMetaForAppointmentType(type?: AppointmentTypeOption | null) {
  const docs = type?.serviceDocumentation && typeof type.serviceDocumentation === 'object' ? type.serviceDocumentation as Record<string, any> : {};
  const meta = docs.serviceMeta && typeof docs.serviceMeta === 'object' ? docs.serviceMeta as Record<string, any> : {};
  return {
    isClass: Boolean(meta.isClass),
    classCapacity: Number(meta.classCapacity || 8),
    classNotificationsEnabled: Boolean(meta.classNotificationsEnabled ?? true),
    classPortalVisible: Boolean(meta.classPortalVisible ?? true),
    classParticipantInstructions: String(meta.classParticipantInstructions || ''),
  };
}

function timingMetaForAppointmentType(type?: AppointmentTypeOption | null) {
  const docs = type?.serviceDocumentation && typeof type.serviceDocumentation === 'object' ? type.serviceDocumentation as Record<string, any> : {};
  const meta = docs.serviceMeta && typeof docs.serviceMeta === 'object' ? docs.serviceMeta as Record<string, any> : {};
  const extraBeforeMinutes = Math.max(0, Number(meta.extraBeforeMinutes || 0));
  const extraAfterMinutes = Math.max(0, Number(meta.extraAfterMinutes || 0));
  return {
    bufferBeforeMinutes: Math.max(0, Number(meta.bufferBeforeMinutes || 0)),
    bufferAfterMinutes: Math.max(0, Number(meta.bufferAfterMinutes || 0)),
    extraBeforeMinutes,
    extraAfterMinutes,
    effectiveDurationMinutes: Math.max(5, Number(type?.durationMinutes || 0) + extraBeforeMinutes + extraAfterMinutes),
  };
}

function classMetaForAppointment(appt?: { appointmentType?: AppointmentTypeOption | null; notes?: string | null } | null) {
  const meta = classMetaForAppointmentType(appt?.appointmentType);
  return {
    ...meta,
    isClass: meta.isClass || /class booking:/i.test(String(appt?.notes || '')),
  };
}

type ProviderOption = {
  id: string;
  name: string;
  avatarUrl?: string | null;
};

type LocationOption = {
  id: string;
  name: string;
  isPrimary?: boolean;
};

type SchedulingHealth = {
  date: string;
  appointments: number;
  noShows: number;
  noShowRate: number;
  remindersDue: number;
  activeWaitlist: number;
  openOffers: number;
};

type MedicalFormOption = {
  id: string;
  name: string;
  code?: string | null;
  category?: string | null;
};

type AppointmentMedicalForm = {
  instanceId: string;
  templateId: string;
  templateName: string;
  status: string;
  sentAt?: string | null;
  completedAt?: string | null;
};



const statusColors: Record<AppointmentStatus, string> = {

  SCHEDULED: 'pill-info',

  PORTAL_BOOKED: 'pill-warn',

  CHECKED_IN: 'pill-success',

  COMPLETED: 'pill-success',

  CANCELLED: 'pill-danger',

  NO_SHOW: 'pill-warn',

};



const statusLabels: Record<AppointmentStatus, string> = {

  SCHEDULED: 'Scheduled',

  PORTAL_BOOKED: 'Portal booked',

  CHECKED_IN: 'Checked-in',

  COMPLETED: 'Completed',

  CANCELLED: 'Cancelled',

  NO_SHOW: 'No-show',

};

function getAppointmentServiceDisplay(appt: Appointment) {
  const sessionTypeName = appt.appointmentType.name;
  const bookedServiceName = appt.billingService?.name?.trim() || '';
  return {
    primaryName: bookedServiceName || sessionTypeName,
    secondaryName: bookedServiceName && bookedServiceName !== sessionTypeName ? sessionTypeName : '',
  };
}



const statusBadgeClasses: Record<AppointmentStatus, string> = {

  SCHEDULED: 'bg-sky-100 text-sky-700 border border-sky-200',

  PORTAL_BOOKED: 'bg-lime-100 text-lime-800 border border-lime-200',

  CHECKED_IN: 'bg-emerald-100 text-emerald-700 border border-emerald-200',

  COMPLETED: 'bg-slate-100 text-slate-700 border border-slate-200',

  CANCELLED: 'bg-rose-100 text-rose-700 border border-rose-200',

  NO_SHOW: 'bg-amber-100 text-amber-700 border border-amber-200',

};



const hours = Array.from({ length: 11 }, (_, i) => 8 + i); // 8am-6pm



function toDate(value: string) {

  return new Date(value);

}



function toLocalInput(date: Date) {

  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000);

  return local.toISOString().slice(0, 16);

}



function isSameDay(a: Date, b: Date) {

  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

}

function startOfMiniCalendarMonth(targetDate: Date) {
  const first = new Date(targetDate.getFullYear(), targetDate.getMonth(), 1);
  const mondayIndex = (first.getDay() + 6) % 7;
  first.setDate(first.getDate() - mondayIndex);
  return first;
}

function getViewRange(targetDate: Date, view: ViewMode) {
  const start = new Date(targetDate);
  const end = new Date(targetDate);

  if (view === 'day') {
    start.setHours(0, 0, 0, 0);
    end.setHours(23, 59, 59, 999);
    return { start, end };
  }

  if (view === 'week') {
    const day = start.getDay();
    const diff = start.getDate() - day + (day === 0 ? -6 : 1); // Monday start
    start.setDate(diff);
    start.setHours(0, 0, 0, 0);
    end.setTime(start.getTime());
    end.setDate(start.getDate() + 6);
    end.setHours(23, 59, 59, 999);
    return { start, end };
  }

  start.setDate(1);
  start.setHours(0, 0, 0, 0);
  end.setFullYear(start.getFullYear(), start.getMonth() + 1, 0);
  end.setHours(23, 59, 59, 999);
  return { start, end };
}

function mergeProviderOptions(current: ProviderOption[], incoming: ProviderOption[]) {
  const combined = new Map<string, ProviderOption>();
  current.forEach((provider) => combined.set(provider.id, provider));
  incoming.forEach((provider) => {
    const existing = combined.get(provider.id);
    if (!existing) {
      combined.set(provider.id, provider);
      return;
    }
    combined.set(provider.id, {
      ...existing,
      name: provider.name || existing.name,
      avatarUrl: provider.avatarUrl ?? existing.avatarUrl ?? null,
    });
  });
  return Array.from(combined.values());
}

const appointmentsRequestInFlight = new Map<string, Promise<any[]>>();

const APPOINTMENT_PROVIDER_FILTER_STORAGE_KEY = 'neoemr.appointments.selectedProviders';
const APPOINTMENT_HOVER_PREVIEW_STORAGE_KEY = 'neoemr.appointments.hoverPreviewEnabled';

type AppointmentTableColumnId = 'time' | 'patient' | 'provider' | 'service' | 'status' | 'actions';

const defaultAppointmentTableColumnOrder: AppointmentTableColumnId[] = ['time', 'patient', 'provider', 'service', 'status', 'actions'];

const appointmentTableColumnWidths: Record<AppointmentTableColumnId, string> = {
  time: '180px',
  patient: 'minmax(0,1.4fr)',
  provider: 'minmax(0,1fr)',
  service: 'minmax(0,1.15fr)',
  status: '120px',
  actions: '156px',
};

type HoverPreviewState = {
  appointment: Appointment;
  anchorRect: DOMRect;
};

function readStoredSelectedProviders(): string[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(APPOINTMENT_PROVIDER_FILTER_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((item): item is string => typeof item === 'string');
  } catch {
    return [];
  }
}

function readStoredHoverPreviewEnabled(): boolean {
  if (typeof window === 'undefined') return true;
  try {
    const raw = window.localStorage.getItem(APPOINTMENT_HOVER_PREVIEW_STORAGE_KEY);
    if (raw === null) return true;
    return raw !== 'false';
  } catch {
    return true;
  }
}



export default function Appointments() {

  const navigate = useNavigate();
  const { tr, language } = useI18n();
  const { serviceVariantsEnabled } = useTreatmentCatalogConfig();
  const locale = language === 'es' ? 'es-ES' : language === 'it' ? 'it-IT' : 'en-US';

  const { getAccessTokenSilently, loginWithRedirect } = useAuth0();

  const [view, setView] = useState<ViewMode>('day');
  const [displayMode, setDisplayMode] = useState<DisplayMode>('calendar');
  const [clinicListOpen, setClinicListOpen] = useState(false);
  const [calendarLocked, setCalendarLocked] = useState(false);
  const [savedPreset, setSavedPreset] = useState<string | null>(null);
  const [showLegend, setShowLegend] = useState(false);
  const [showCalendarSettings, setShowCalendarSettings] = useState(false);
  const [showTableColumnDialog, setShowTableColumnDialog] = useState(false);
  const [showBlockoutDialog, setShowBlockoutDialog] = useState(false);
  const [showBookingWizard, setShowBookingWizard] = useState(false);
  const [showCalendarIntegrations, setShowCalendarIntegrations] = useState(false);
  const [showRebookWaitlistDialog, setShowRebookWaitlistDialog] = useState(false);
  const [showCompactTools, setShowCompactTools] = useState(false);
  const [dayNote, setDayNote] = useState('');
  const [calendarDensity, setCalendarDensity] = useState<'comfortable' | 'compact'>('comfortable');
  const [appointmentOverlapEnabled, setAppointmentOverlapEnabled] = useState(false);
  const [showCancelledAppointments, setShowCancelledAppointments] = useState(true);
  const [calendarConfiguration, setCalendarConfiguration] = useState<CalendarConfiguration>({ defaultDisplayMode: 'calendar', visibleHoursStart: 8, visibleHoursEnd: 18, showProviderAvatars: true, showLocationFilter: true });
  const [appointmentSettingsConfig, setAppointmentSettingsConfig] = useState<AppointmentSettingsConfig>({ requireService: true, requireRoom: false, allowClientCardAccess: true, defaultStatus: 'BOOKED', bookingRuleSummary: '' });
  const [shiftNotes, setShiftNotes] = useState<ShiftNote[]>([]);
  const [dayNotes, setDayNotes] = useState<DayNote[]>([]);
  const [calendarPresets, setCalendarPresets] = useState<CalendarPreset[]>([]);
  const [externalCalendarSync, setExternalCalendarSync] = useState<ExternalCalendarSync[]>([]);
  const [clinicListSettings, setClinicListSettings] = useState<ClinicListSettings>({ enabled: true, includeStatus: true, includeLocation: true, includeService: true, defaultOpen: false });
  const [generatedClinicList, setGeneratedClinicList] = useState<{ generatedAt: string; rows: ClinicListRow[] } | null>(null);
  const [providerColumnOrder, setProviderColumnOrder] = useState<string[]>([]);
  const [calendarLockingEnabled, setCalendarLockingEnabled] = useState(true);
  const [calendarLockPasscode, setCalendarLockPasscode] = useState('1234');
  const [calendarPasscodeRules, setCalendarPasscodeRules] = useState<CalendarPasscodeRules>({ minLength: 4, requireNumbers: true, lockOnIdle: true, idleTimeoutMinutes: 15 });
  const [calendarActivities, setCalendarActivities] = useState<CalendarActivity[]>([]);
  const [calendarBlockouts, setCalendarBlockouts] = useState<CalendarBlockout[]>([]);
  const [appointmentIconLegend, setAppointmentIconLegend] = useState<AppointmentIconLegendItem[]>(defaultAppointmentIconLegend);
  const [wizardClientMode, setWizardClientMode] = useState<'existing' | 'quick-create'>('existing');
  const [wizardSelectedServices, setWizardSelectedServices] = useState<string[]>([]);
  const [wizardPackageMode, setWizardPackageMode] = useState(false);
  const [rescheduleAuditNote, setRescheduleAuditNote] = useState('');
  const [waitlistNotifications, setWaitlistNotifications] = useState(true);
  const [resourceRulesEnabled, setResourceRulesEnabled] = useState(true);

  const [date, setDate] = useState(new Date());

  const [appointments, setAppointments] = useState<Appointment[]>([]);

  const [providerOptions, setProviderOptions] = useState<ProviderOption[]>([]);

  const [patientOptions, setPatientOptions] = useState<
    Array<{ id: string; name: string; avatarUrl?: string | null; mrn?: string | null; phone?: string | null; dob?: string | null }>
  >([]);
  const [roomOptions, setRoomOptions] = useState<Array<{ id: string; name: string; type: string; locationId?: string | null }>>([]);
  const [assetOptions, setAssetOptions] = useState<Array<{ id: string; name: string; type: string; locationId?: string | null }>>([]);
  const [locationOptions, setLocationOptions] = useState<LocationOption[]>([]);
  const [selectedLocations, setSelectedLocations] = useState<string[]>([]);
  const [selectedServices, setSelectedServices] = useState<string[]>([]);

  const [selectedProviders, setSelectedProviders] = useState<string[]>(() => readStoredSelectedProviders());

  const [filterStatus, setFilterStatus] = useState<AppointmentStatus | 'all'>('all');

  const [hovered, setHovered] = useState<HoverPreviewState | null>(null);
  const [hoverPreviewEnabled, setHoverPreviewEnabled] = useState<boolean>(() => readStoredHoverPreviewEnabled());
  const [appointmentTableColumnOrder, setAppointmentTableColumnOrder] = useState<AppointmentTableColumnId[]>(defaultAppointmentTableColumnOrder);
  const [tableActionMenuId, setTableActionMenuId] = useState<string | null>(null);
  const hoverHideTimeoutRef = useRef<number | null>(null);

  const [modalOpen, setModalOpen] = useState(false);

  const [loading, setLoading] = useState(false);

  const [saving, setSaving] = useState(false);

  const [error, setError] = useState<string | null>(null);

  const [defaultSlotMinutes, setDefaultSlotMinutes] = useState(30);
  const [schedulingHealth, setSchedulingHealth] = useState<SchedulingHealth | null>(null);
  const [automationBusyId, setAutomationBusyId] = useState<string | null>(null);
  const [pendingBlockout, setPendingBlockout] = useState<PendingBlockout>(() => ({
    title: 'Time off',
    start: toLocalInput(new Date()),
    durationMinutes: 60,
    providerIds: [],
    reason: '',
    repeatEnabled: false,
    repeatFrequency: 'weekly',
    repeatCount: 1,
  }));

  const clearHoverHideTimeout = useCallback(() => {
    if (hoverHideTimeoutRef.current !== null && typeof window !== 'undefined') {
      window.clearTimeout(hoverHideTimeoutRef.current);
      hoverHideTimeoutRef.current = null;
    }
  }, []);

  const openHoverPreview = useCallback(
    (appointment: Appointment, anchorRect: DOMRect) => {
      if (!hoverPreviewEnabled) return;
      clearHoverHideTimeout();
      setHovered({ appointment, anchorRect });
    },
    [clearHoverHideTimeout, hoverPreviewEnabled],
  );

  const scheduleHoverPreviewClose = useCallback(() => {
    clearHoverHideTimeout();
    if (typeof window === 'undefined') {
      setHovered(null);
      return;
    }
    hoverHideTimeoutRef.current = window.setTimeout(() => {
      setHovered(null);
      hoverHideTimeoutRef.current = null;
    }, 180);
  }, [clearHoverHideTimeout]);

  const [pending, setPending] = useState<{

    id?: string;
    encounterId?: string | null;

    patientId: string;

    providerId: string;

    status: AppointmentStatus;

    appointmentTypeId: string;
    billingServiceId?: string;
    locationId: string;

    start: string;

    durationMinutes: number;

    title: string;

    isTelemed?: boolean;

    generateTelemedLink?: boolean;

    joinUrl?: string | null;
    roomResourceId?: string;
    assetResourceIds: string[];
    additionalStaffIds: string[];
    medicalFormTemplateIds: string[];
    medicalForms?: AppointmentMedicalForm[];
    emailReminderEnabled: boolean;
    smsReminderEnabled: boolean;
    reminderMinutesBefore: number;
    multiServiceIds: string[];
    packageBookingEnabled: boolean;
    packageId: string;
    packageQuantity: number;
    classBookingEnabled: boolean;
    classParticipantIds: string[];
    classParticipantStatuses: Record<string, AppointmentStatus>;
    classCapacity: number;
    classNotificationsEnabled: boolean;
    repeatEnabled: boolean;
    repeatFrequency: 'daily' | 'weekly' | 'monthly';
    repeatCount: number;
    bookingDepositEnabled: boolean;
    bookingDepositAmount: number;
    paymentHoldEnabled: boolean;
    paymentHoldReference: string;

  }>({

    id: undefined,
    encounterId: null,

    patientId: '',

    providerId: '',

    status: 'SCHEDULED' as AppointmentStatus,

    appointmentTypeId: '',
    billingServiceId: '',
    locationId: '',

    start: toLocalInput(new Date()),

    durationMinutes: 30,

    title: '',

    isTelemed: false,

    generateTelemedLink: false,

    joinUrl: null,
    roomResourceId: '',
    assetResourceIds: [],
    additionalStaffIds: [],
    medicalFormTemplateIds: [],
    medicalForms: [],
    emailReminderEnabled: false,
    smsReminderEnabled: false,
    reminderMinutesBefore: 1440,
    multiServiceIds: [],
    packageBookingEnabled: false,
    packageId: '',
    packageQuantity: 1,
    classBookingEnabled: false,
    classParticipantIds: [],
    classParticipantStatuses: {},
    classCapacity: 8,
    classNotificationsEnabled: true,
    repeatEnabled: false,
    repeatFrequency: 'weekly',
    repeatCount: 1,
    bookingDepositEnabled: false,
    bookingDepositAmount: 0,
    paymentHoldEnabled: false,
    paymentHoldReference: '',

  });

  const { createTelemedSession } = useTelemedApi();

  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    const openId = searchParams.get('openId');
    if (openId && appointments.length > 0 && !modalOpen) {
      const appt = appointments.find((a) => a.id === openId);
      if (appt) {
        setPending({
          id: appt.id,
          encounterId: appt.encounterId ?? null,
          patientId: appt.patientId,
          providerId: appt.providerId,
          status: appt.status,
          appointmentTypeId: appt.appointmentType.id,
          billingServiceId: serviceVariantsEnabled ? appt.billingService?.id ?? '' : '',
          locationId: appt.locationId || locationOptions.find((location) => location.isPrimary)?.id || locationOptions[0]?.id || '',
          start: toLocalInput(new Date(appt.start)),
          durationMinutes: appt.appointmentType.durationMinutes,
          title: appt.title || '',
          isTelemed: appt.isTelemed,
          generateTelemedLink: false,
          joinUrl: appt.joinUrl,
          roomResourceId: appt.roomResourceId ?? '',
          assetResourceIds: appt.assetResourceIds ?? [],
          additionalStaffIds: appt.additionalStaffIds ?? [],
          medicalFormTemplateIds: appt.medicalForms?.map((item) => item.templateId) ?? [],
          medicalForms: appt.medicalForms ?? [],
          emailReminderEnabled: Boolean(appt.emailReminderEnabled),
          smsReminderEnabled: Boolean(appt.smsReminderEnabled),
          reminderMinutesBefore: appt.reminderMinutesBefore ?? 1440,
          multiServiceIds: [],
          packageBookingEnabled: false,
          packageId: '',
          packageQuantity: 1,
          classBookingEnabled: false,
          classParticipantIds: [],
          classParticipantStatuses: {},
          classCapacity: 8,
          classNotificationsEnabled: true,
          repeatEnabled: false,
          repeatFrequency: 'weekly',
          repeatCount: 1,
          bookingDepositEnabled: false,
          bookingDepositAmount: 0,
          paymentHoldEnabled: false,
          paymentHoldReference: '',
        });
        setModalOpen(true);
        // Remove param so it doesn't reopen on refresh/navigation
        setSearchParams({}, { replace: true });
      }
    }
  }, [appointments, searchParams, modalOpen, setSearchParams, locationOptions, serviceVariantsEnabled]);



  const getToken = useCallback(async () => {

    try {

      return await getAccessTokenSilently({

        authorizationParams: { audience: AUTH_AUDIENCE },

      });

    } catch (err: any) {

      if (err?.error === 'login_required') {

        await loginWithRedirect({ appState: { returnTo: window.location.pathname } });

        return '';

      }

      throw err;

    }

  }, [getAccessTokenSilently, loginWithRedirect]);



  const loadAppointments = useCallback(async () => {



    setLoading(true);

    setError(null);

    try {

      const token = await getToken();

      if (!token) {

        setLoading(false);

        return;

      }

      const { start, end } = getViewRange(date, view);
      const params = new URLSearchParams({
        start: start.toISOString(),
        end: end.toISOString(),
      });
      if (selectedProviders.length === 1) {
        params.set('providerId', selectedProviders[0]);
      } else if (selectedProviders.length > 1) {
        params.set('providerIds', selectedProviders.join(','));
      }
      if (filterStatus !== 'all') {
        params.set('status', filterStatus);
      }

      const requestUrl = `${API_BASE}/appointments?${params.toString()}`;
      const inFlight = appointmentsRequestInFlight.get(requestUrl);
      const requestPromise =
        inFlight ??
        fetch(requestUrl, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        }).then(async (res) => {
          if (!res.ok) throw new Error(`Load failed ${res.status}`);
          return (await res.json()) as any[];
        });
      if (!inFlight) {
        appointmentsRequestInFlight.set(requestUrl, requestPromise);
      }
      const data = await requestPromise.finally(() => {
        if (appointmentsRequestInFlight.get(requestUrl) === requestPromise) {
          appointmentsRequestInFlight.delete(requestUrl);
        }
      });

      const mapped: Appointment[] = data.map((a: any) => ({

        id: a.id,

        patientId: a.patient.id,
        encounterId: a.encounterId ?? null,

        patient: a.patient.name,
        patientAvatarUrl:
          a.patient?.avatarUrl ??
          a.patient?.photoUrl ??
          a.patient?.imageUrl ??
          a.patient?.profilePhotoUrl ??
          a.patient?.profileImageUrl ??
          null,

        providerId: a.provider.id,

        provider: a.provider.name,
        providerAvatarUrl: a.provider?.avatarUrl ?? null,

        status: a.status as AppointmentStatus,

        appointmentType: {

          id: a.appointmentType?.id ?? '',

          name: a.appointmentType?.name ?? 'Appointment',

          color: a.appointmentType?.color ?? '#0ea5e9',

          durationMinutes: a.appointmentType?.durationMinutes ?? 30,

          isTelemed: a.appointmentType?.isTelemed,

          serviceVariants: Array.isArray(a.appointmentType?.serviceVariants) ? a.appointmentType.serviceVariants : [],

        },

        billingService: a.billingService
          ? {
              id: a.billingService.id,
              name: a.billingService.name,
              category: a.billingService.category ?? null,
              price: Number(a.billingService.price ?? 0),
            }
          : null,

        start: a.startTime,

        end: a.endTime,

        title: a.title ?? null,

        isTelemed: Boolean(a.isTelemed || a.appointmentType?.isTelemed),

        visitStatus: a.visitStatus ?? a.telemed?.visitStatus ?? null,

        joinUrl: a.joinUrl ?? a.telemed?.joinUrl ?? null,

        hostToken: a.hostToken ?? a.telemed?.hostToken ?? null,

        guestToken: a.guestToken ?? a.telemed?.guestToken ?? null,

        roomName: a.roomName ?? a.telemed?.roomName ?? null,
        roomResourceId: a.roomResourceId ?? null,
        assetResourceIds: Array.isArray(a.assetResourceIds) ? a.assetResourceIds : [],
        additionalStaffIds: Array.isArray(a.additionalStaffIds) ? a.additionalStaffIds : [],
        locationId: a.location?.id ?? null,
        location: a.location ?? null,
        medicalForms: Array.isArray(a.medicalForms)
          ? a.medicalForms.map((form: any) => ({
              instanceId: form.instanceId,
              templateId: form.templateId,
              templateName: form.templateName,
              status: form.status,
              sentAt: form.sentAt ?? null,
              completedAt: form.completedAt ?? null,
            }))
          : [],
        source: a.source === 'PORTAL' ? 'PORTAL' : 'STAFF',
        confirmedAt: a.confirmedAt ?? null,
        checkedInAt: a.checkedInAt ?? null,
        arrivedAt: a.arrivedAt ?? null,
        patientInstructionsAckAt: a.patientInstructionsAckAt ?? null,
        noShowRiskScore: typeof a.noShowRiskScore === 'number' ? a.noShowRiskScore : null,
        reminderSummary: a.reminderSummary ?? { pending: 0, sent: 0, failed: 0 },
        careTaskSummary: a.careTaskSummary ?? { pending: 0, completed: 0 },
        emailReminderEnabled: Boolean(a.emailReminderEnabled),
        smsReminderEnabled: Boolean(a.smsReminderEnabled),
        reminderMinutesBefore: a.reminderMinutesBefore ?? null,

      }));

      setAppointments(mapped);

      const fromAppts: ProviderOption[] = mapped.map((appointment) => ({
        id: appointment.providerId,
        name: appointment.provider,
        avatarUrl: appointment.providerAvatarUrl ?? null,
      }));
      setProviderOptions((prev) => mergeProviderOptions(prev, fromAppts));

    } catch (err: any) {

      setError(err.message || 'Failed to load appointments');

    } finally {

      setLoading(false);

    }

  }, [getToken, date, view, selectedProviders, filterStatus]);

  const loadSchedulingHealth = useCallback(async () => {
    try {
      const token = await getToken();
      if (!token) return;
      const dateKey = toLocalInput(date).slice(0, 10);
      const res = await fetch(`${API_BASE}/scheduling-automation/health?date=${dateKey}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return;
      setSchedulingHealth(await res.json());
    } catch {
      // non-critical operational summary
    }
  }, [date, getToken]);

  const runAppointmentAutomation = useCallback(
    async (appointmentId: string, action: 'schedule-reminders' | 'care-tasks' | 'check-in' | 'arrive' | 'confirm' | 'no-show') => {
      try {
        const token = await getToken();
        if (!token) return;
        setAutomationBusyId(`${appointmentId}:${action}`);
        const endpoint =
          action === 'schedule-reminders'
            ? `${API_BASE}/scheduling-automation/appointments/${appointmentId}/reminders/schedule`
            : action === 'care-tasks'
              ? `${API_BASE}/scheduling-automation/appointments/${appointmentId}/care-tasks/defaults`
              : `${API_BASE}/scheduling-automation/appointments/${appointmentId}/lifecycle`;
        const res = await fetch(endpoint, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            ...(action === 'schedule-reminders' || action === 'care-tasks' ? {} : { 'Content-Type': 'application/json' }),
          },
          body:
            action === 'schedule-reminders' || action === 'care-tasks'
              ? undefined
              : JSON.stringify({ action }),
        });
        if (!res.ok) throw new Error(`Automation failed ${res.status}`);
        await Promise.all([loadAppointments(), loadSchedulingHealth()]);
      } catch (err: any) {
        setError(err?.message || 'Scheduling automation failed');
      } finally {
        setAutomationBusyId(null);
      }
    },
    [getToken, loadAppointments, loadSchedulingHealth],
  );

  const startOrOpenVisit = useCallback(
    async (appt: Appointment) => {
      if (appt.encounterId) {
        navigate(`/visits/${appt.encounterId}`);
        return;
      }
      try {
        const token = await getToken();
        if (!token) return;
        setAutomationBusyId(`${appt.id}:visit`);
        const res = await fetch(`${API_BASE}/encounters/start-from-appointment`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ appointmentId: appt.id }),
        });
        if (!res.ok) throw new Error(await res.text());
        const result = await res.json();
        navigate(`/visits/${result.encounterId}`);
        void loadAppointments();
      } catch (err: any) {
        setError(err?.message || 'Unable to open visit.');
      } finally {
        setAutomationBusyId(null);
      }
    },
    [getToken, loadAppointments, navigate],
  );



  const loadPatients = useCallback(async () => {

    try {

      const token = await getToken();



      if (!token) return;

      const res = await fetch(`${API_BASE}/patients?page=1&pageSize=100`, {

        headers: token ? { Authorization: `Bearer ${token}` } : {},

      });

      if (!res.ok) throw new Error(`Load patients failed ${res.status}`);

      const data = await res.json();

      setPatientOptions(

        data.data.map((p: any) => ({

          id: p.id,

          name: `${p.firstName ?? ''} ${p.lastName ?? ''}`.trim() || p.mrn || 'Patient',
          avatarUrl: p.avatarUrl ?? p.photoUrl ?? p.imageUrl ?? p.profilePhotoUrl ?? p.profileImageUrl ?? null,
          mrn: p.mrn ?? null,
          phone: p.phone ?? p.mobilePhone ?? p.contactPhone ?? null,
          dob: p.dob ?? p.dateOfBirth ?? null,

        })),

      );

    } catch (err: any) {

      setError(err.message || 'Failed to load patients');

    }

  }, [getToken]);

  const loadProviders = useCallback(async () => {
    try {
      const token = await getToken();
      if (!token) return;
      const providersRes = await fetch(`${API_BASE}/providers`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (providersRes.ok) {
        const providers = await providersRes.json();
        const normalized: ProviderOption[] = (Array.isArray(providers) ? providers : [])
          .map((provider: any) => ({
            id: typeof provider.userId === 'string' && provider.userId.trim() ? provider.userId.trim() : '',
            name: `${provider.firstName ?? ''} ${provider.lastName ?? ''}`.trim() || 'Provider',
            avatarUrl: provider.avatarUrl ?? null,
          }))
          .filter((provider: ProviderOption) => Boolean(provider.id));
        if (normalized.length > 0) {
          setProviderOptions((prev) => mergeProviderOptions(prev, normalized));
          return;
        }
      }
    } catch {
      // ignore silently
    }
  }, [getToken]);

  const loadResources = useCallback(async () => {
    try {
      const token = await getToken();
      if (!token) return;
      const res = await fetch(`${API_BASE}/resources`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return;
      const resources = await res.json();
      const activeResources = resources.filter((resource: any) => resource.active !== false);
      const rooms = activeResources.filter((resource: any) => /room|suite|bay|bed|ward|or|theatre|theater|icu/i.test(resource.type ?? ''));
      const assets = activeResources.filter((resource: any) => !rooms.some((room: any) => room.id === resource.id));
      setRoomOptions(rooms.map((resource: any) => ({ id: resource.id, name: resource.name, type: resource.type, locationId: resource.locationId ?? null })));
      setAssetOptions(assets.map((resource: any) => ({ id: resource.id, name: resource.name, type: resource.type, locationId: resource.locationId ?? null })));
    } catch {
      // ignore silently
    }
  }, [getToken]);

  const loadLocations = useCallback(async () => {
    try {
      const token = await getToken();
      if (!token) return;
      const res = await fetch(`${API_BASE}/locations`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return;
      const locations = await res.json();
      setLocationOptions(
        (Array.isArray(locations) ? locations : []).map((location: any) => ({
          id: location.id,
          name: location.name,
          isPrimary: Boolean(location.isPrimary),
        })),
      );
    } catch {
      // ignore silently
    }
  }, [getToken]);

  const loadMedicalForms = useCallback(async () => {
    try {
      const token = await getToken();
      if (!token) return;
      const res = await fetch(`${API_BASE}/medical-forms/templates`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return;
      const templates = await res.json();
      setMedicalFormOptions(
        templates
          .filter((template: any) => template.status !== 'ARCHIVED')
          .map((template: any) => ({
            id: template.id,
            name: template.name,
            code: template.code ?? null,
            category: template.category ?? null,
          })),
      );
    } catch {
      // ignore silently
    }
  }, [getToken]);



  const [appointmentTypes, setAppointmentTypes] = useState<AppointmentTypeOption[]>([]);
  const [medicalFormOptions, setMedicalFormOptions] = useState<MedicalFormOption[]>([]);



  // ... (existing state)



  const loadSettings = useCallback(async () => {

    try {

      const token = await getToken();

      if (!token) return;

      const [schedulingRes, typesRes] = await Promise.all([

        fetch(`${API_BASE}/scheduling-settings`, { headers: { Authorization: `Bearer ${token}` } }),

        fetch(`${API_BASE}/appointment-types`, { headers: { Authorization: `Bearer ${token}` } }),

      ]);



      if (schedulingRes.ok) {

        const scheduling = await schedulingRes.json();

        setDefaultSlotMinutes(scheduling.defaultSlotMinutes ?? 30);
        if (scheduling.defaultView) {
          setView(scheduling.defaultView);
        }
        setCalendarLockingEnabled(scheduling.calendarLockingEnabled ?? true);
        setCalendarLockPasscode(scheduling.calendarLockPasscode || '1234');
        setCalendarPasscodeRules({
          minLength: scheduling.calendarPasscodeRules?.minLength ?? 4,
          requireNumbers: scheduling.calendarPasscodeRules?.requireNumbers ?? true,
          lockOnIdle: scheduling.calendarPasscodeRules?.lockOnIdle ?? true,
          idleTimeoutMinutes: scheduling.calendarPasscodeRules?.idleTimeoutMinutes ?? 15,
        });
        setCalendarActivities(Array.isArray(scheduling.calendarActivities) ? scheduling.calendarActivities : []);
        setCalendarBlockouts(Array.isArray(scheduling.calendarBlockouts) ? scheduling.calendarBlockouts : []);
        setAppointmentIconLegend(Array.isArray(scheduling.appointmentIconLegend) ? scheduling.appointmentIconLegend : defaultAppointmentIconLegend);
        setAppointmentOverlapEnabled(scheduling.appointmentOverlapEnabled ?? false);
        if (scheduling.calendarAppearance) {
          setCalendarDensity(scheduling.calendarAppearance.density ?? 'comfortable');
          setShowCancelledAppointments(scheduling.calendarAppearance.showCancelledAppointments ?? true);
          setShowLegend(scheduling.calendarAppearance.showAppointmentLegend ?? false);
        }
        setCalendarConfiguration({
          defaultDisplayMode: scheduling.calendarConfiguration?.defaultDisplayMode ?? 'calendar',
          visibleHoursStart: scheduling.calendarConfiguration?.visibleHoursStart ?? 8,
          visibleHoursEnd: scheduling.calendarConfiguration?.visibleHoursEnd ?? 18,
          showProviderAvatars: scheduling.calendarConfiguration?.showProviderAvatars ?? true,
          showLocationFilter: scheduling.calendarConfiguration?.showLocationFilter ?? true,
        });
        setDisplayMode(scheduling.calendarConfiguration?.defaultDisplayMode ?? 'calendar');
        setAppointmentSettingsConfig({
          requireService: scheduling.appointmentSettings?.requireService ?? true,
          requireRoom: scheduling.appointmentSettings?.requireRoom ?? false,
          allowClientCardAccess: scheduling.appointmentSettings?.allowClientCardAccess ?? true,
          defaultStatus: scheduling.appointmentSettings?.defaultStatus ?? 'BOOKED',
          bookingRuleSummary: scheduling.appointmentSettings?.bookingRuleSummary ?? '',
        });
        setShiftNotes(Array.isArray(scheduling.shiftNotes) ? scheduling.shiftNotes : []);
        setDayNotes(Array.isArray(scheduling.dayNotes) ? scheduling.dayNotes : []);
        setCalendarPresets(Array.isArray(scheduling.calendarPresets) ? scheduling.calendarPresets : []);
        if (Array.isArray(scheduling.calendarColumnOrder?.tableColumns)) {
          const validColumns = scheduling.calendarColumnOrder.tableColumns.filter((columnId: string): columnId is AppointmentTableColumnId =>
            defaultAppointmentTableColumnOrder.includes(columnId as AppointmentTableColumnId),
          );
          if (validColumns.length === defaultAppointmentTableColumnOrder.length) {
            setAppointmentTableColumnOrder(validColumns);
          }
        }
        setProviderColumnOrder(Array.isArray(scheduling.calendarColumnOrder?.providerIds) ? scheduling.calendarColumnOrder.providerIds : []);
        setSelectedProviders(Array.isArray(scheduling.calendarFilters?.staffIds) ? scheduling.calendarFilters.staffIds : readStoredSelectedProviders());
        setSelectedLocations(Array.isArray(scheduling.calendarFilters?.locationIds) ? scheduling.calendarFilters.locationIds : []);
        setSelectedServices(Array.isArray(scheduling.calendarFilters?.serviceIds) ? scheduling.calendarFilters.serviceIds : []);
        setExternalCalendarSync(Array.isArray(scheduling.externalCalendarSync) ? scheduling.externalCalendarSync : []);
        setClinicListSettings({
          enabled: scheduling.clinicListSettings?.enabled ?? true,
          includeStatus: scheduling.clinicListSettings?.includeStatus ?? true,
          includeLocation: scheduling.clinicListSettings?.includeLocation ?? true,
          includeService: scheduling.clinicListSettings?.includeService ?? true,
          defaultOpen: scheduling.clinicListSettings?.defaultOpen ?? false,
        });
        setClinicListOpen(scheduling.clinicListSettings?.defaultOpen ?? false);

        setPending((prev) => ({

          ...prev,

          durationMinutes: prev.id ? prev.durationMinutes : scheduling.defaultSlotMinutes ?? prev.durationMinutes,

        }));

      }



      if (typesRes.ok) {

        const typeData: AppointmentTypeOption[] = await typesRes.json();

        setAppointmentTypes(typeData);

        setPending((prev) => {

          const nextTypeId = prev.appointmentTypeId || typeData[0]?.id || '';

          const match = typeData.find((t) => t.id === nextTypeId) ?? typeData[0];
          const serviceVariants = Array.isArray(match?.serviceVariants)
            ? match.serviceVariants.filter((variant) => variant.active !== false)
            : [];
          const currentServiceStillValid = serviceVariants.some((variant) => variant.id === prev.billingServiceId);

          return {

            ...prev,

            appointmentTypeId: nextTypeId,

            billingServiceId: serviceVariantsEnabled
              ? currentServiceStillValid ? prev.billingServiceId : serviceVariants[0]?.id || ''
              : '',

            durationMinutes: prev.id ? prev.durationMinutes : match?.durationMinutes ?? prev.durationMinutes,

            isTelemed: match?.isTelemed ?? prev.isTelemed,

            generateTelemedLink: match?.isTelemed ?? prev.generateTelemedLink,

          };

        });

      }

    } catch {

      // ignore settings load failure

    }

  }, [getToken, serviceVariantsEnabled]);

  const toggleCalendarLock = useCallback(() => {
    if (!calendarLockingEnabled) {
      setCalendarLocked((current) => !current);
      return;
    }
    if (!calendarLocked) {
      setCalendarLocked(true);
      return;
    }
    const entered = window.prompt(tr('Enter calendar passcode'));
    if (entered === calendarLockPasscode) {
      setCalendarLocked(false);
      setError(null);
    } else {
      setError(tr('Calendar passcode is incorrect.'));
    }
  }, [calendarLocked, calendarLockPasscode, calendarLockingEnabled, tr]);

  const saveSchedulingPatch = useCallback(async (patch: Record<string, unknown>) => {
    const token = await getToken();
    if (!token) return;
    await fetch(`${API_BASE}/scheduling-settings`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(patch),
    });
  }, [getToken]);

  const selectedDateKey = useMemo(() => date.toISOString().slice(0, 10), [date]);

  useEffect(() => {
    const match = dayNotes.find((note) => note.date === selectedDateKey);
    setDayNote(match?.note || '');
  }, [dayNotes, selectedDateKey]);

  const saveDayNote = useCallback(async () => {
    const trimmed = dayNote.trim();
    const nextNotes = dayNotes.filter((note) => note.date !== selectedDateKey);
    if (trimmed) {
      nextNotes.push({ id: `day-${selectedDateKey}`, date: selectedDateKey, note: trimmed });
    }
    setDayNotes(nextNotes);
    await saveSchedulingPatch({ dayNotes: nextNotes });
  }, [dayNote, dayNotes, saveSchedulingPatch, selectedDateKey]);

  const saveCurrentCalendarPreset = useCallback(async () => {
    const preset: CalendarPreset = {
      id: `preset-${Date.now()}`,
      name: `${view} ${filterStatus === 'all' ? 'all appointments' : filterStatus}`,
      view,
      displayMode,
      filterStatus,
      providerIds: selectedProviders,
      locationIds: selectedLocations,
    };
    const nextPresets = [...calendarPresets, preset];
    setCalendarPresets(nextPresets);
    setSavedPreset(preset.name);
    await saveSchedulingPatch({ calendarPresets: nextPresets });
  }, [calendarPresets, displayMode, filterStatus, saveSchedulingPatch, selectedLocations, selectedProviders, view]);

  function applyCalendarPreset(preset: CalendarPreset) {
    setView(preset.view);
    if (preset.displayMode) setDisplayMode(preset.displayMode);
    if (preset.filterStatus) setFilterStatus(preset.filterStatus as AppointmentStatus | 'all');
    setSelectedProviders(preset.providerIds || []);
    setSelectedLocations(preset.locationIds || []);
    setSavedPreset(preset.name);
  }

  const updateExternalSync = useCallback(async (id: string, patch: Partial<ExternalCalendarSync>) => {
    const nextSync = externalCalendarSync.map((sync) => sync.id === id ? { ...sync, ...patch, lastSyncedAt: patch.enabled ? new Date().toISOString() : sync.lastSyncedAt } : sync);
    setExternalCalendarSync(nextSync);
    await saveSchedulingPatch({ externalCalendarSync: nextSync });
  }, [externalCalendarSync, saveSchedulingPatch]);

  const saveCalendarBlockouts = useCallback(async (nextBlockouts: CalendarBlockout[]) => {
    setCalendarBlockouts(nextBlockouts);
    await saveSchedulingPatch({ calendarBlockouts: nextBlockouts });
  }, [saveSchedulingPatch]);

  const openBlockoutDialogAt = useCallback((dt: Date, providerId?: string) => {
    if (calendarLocked) {
      setError(tr('Calendar is locked. Unlock it before creating or moving blockouts.'));
      return;
    }
    setPendingBlockout({
      title: tr('Time off'),
      start: toLocalInput(dt),
      durationMinutes: defaultSlotMinutes || 60,
      providerIds: providerId ? [providerId] : selectedProviders.length ? selectedProviders : providerOptions[0]?.id ? [providerOptions[0].id] : [],
      reason: '',
      repeatEnabled: false,
      repeatFrequency: 'weekly',
      repeatCount: 1,
    });
    setShowBlockoutDialog(true);
  }, [calendarLocked, defaultSlotMinutes, providerOptions, selectedProviders, tr]);

  const editBlockout = useCallback((blockout: CalendarBlockout) => {
    const start = toDate(blockout.startTime);
    const end = toDate(blockout.endTime);
    setPendingBlockout({
      id: blockout.id,
      seriesId: blockout.seriesId,
      title: blockout.title,
      start: toLocalInput(start),
      durationMinutes: Math.max(5, Math.round((end.getTime() - start.getTime()) / 60000)),
      providerIds: blockout.providerIds,
      reason: blockout.reason || '',
      repeatEnabled: false,
      repeatFrequency: blockout.repeatFrequency && blockout.repeatFrequency !== 'none' ? blockout.repeatFrequency : 'weekly',
      repeatCount: blockout.repeatCount || 1,
    });
    setShowBlockoutDialog(true);
  }, []);

  const saveBlockout = useCallback(async () => {
    if (!pendingBlockout.title.trim() || !pendingBlockout.providerIds.length) {
      setError(tr('Blockout title and at least one staff member are required.'));
      return;
    }
    const start = new Date(pendingBlockout.start);
    if (Number.isNaN(start.getTime())) {
      setError(tr('Blockout start time is invalid.'));
      return;
    }
    const repeatCount = pendingBlockout.id ? 1 : pendingBlockout.repeatEnabled ? Math.min(Math.max(Number(pendingBlockout.repeatCount) || 1, 1), 52) : 1;
    const seriesId = pendingBlockout.id ? pendingBlockout.seriesId : repeatCount > 1 ? `blockout-series-${Date.now()}` : undefined;
    const now = new Date().toISOString();
    const nextEntries: CalendarBlockout[] = Array.from({ length: repeatCount }, (_, index) => {
      const nextStart = new Date(start);
      if (index > 0) {
        if (pendingBlockout.repeatFrequency === 'daily') nextStart.setDate(nextStart.getDate() + index);
        else if (pendingBlockout.repeatFrequency === 'monthly') nextStart.setMonth(nextStart.getMonth() + index);
        else nextStart.setDate(nextStart.getDate() + index * 7);
      }
      const nextEnd = new Date(nextStart.getTime() + Math.max(5, pendingBlockout.durationMinutes) * 60000);
      return {
        id: pendingBlockout.id || `blockout-${Date.now()}-${index}`,
        seriesId,
        title: pendingBlockout.title.trim(),
        startTime: nextStart.toISOString(),
        endTime: nextEnd.toISOString(),
        providerIds: pendingBlockout.providerIds,
        reason: pendingBlockout.reason.trim() || undefined,
        repeatFrequency: repeatCount > 1 ? pendingBlockout.repeatFrequency : 'none',
        repeatCount,
        createdAt: calendarBlockouts.find((item) => item.id === pendingBlockout.id)?.createdAt || now,
        updatedAt: now,
      };
    });
    const next = pendingBlockout.id
      ? calendarBlockouts.map((item) => (item.id === pendingBlockout.id ? nextEntries[0] : item))
      : [...calendarBlockouts, ...nextEntries];
    await saveCalendarBlockouts(next);
    setShowBlockoutDialog(false);
  }, [calendarBlockouts, pendingBlockout, saveCalendarBlockouts, tr]);

  const deleteBlockout = useCallback(async (blockoutId: string, mode: 'single' | 'series' = 'single') => {
    const target = calendarBlockouts.find((item) => item.id === blockoutId);
    if (!target) return;
    const next = mode === 'series' && target.seriesId
      ? calendarBlockouts.filter((item) => item.seriesId !== target.seriesId)
      : calendarBlockouts.filter((item) => item.id !== blockoutId);
    await saveCalendarBlockouts(next);
    setShowBlockoutDialog(false);
  }, [calendarBlockouts, saveCalendarBlockouts]);



  // ... (existing effects)



  // Helper to get color for a type

  const getTypeColor = (typeId: string) => {

    const type = appointmentTypes.find((t) => t.id === typeId);

    return type?.color || '#cbd5e1';

  };



  // ...



  useEffect(() => {

    loadPatients();
    loadProviders();
    loadResources();
    loadLocations();
    loadMedicalForms();

    loadSettings();

  }, [loadLocations, loadMedicalForms, loadPatients, loadProviders, loadResources, loadSettings]);

  useEffect(() => {
    loadAppointments();
  }, [loadAppointments]);

  useEffect(() => {
    if (!calendarLockingEnabled || !calendarPasscodeRules.lockOnIdle) return;
    if (typeof window === 'undefined') return;
    let timeoutId: number | undefined;
    const armTimer = () => {
      if (timeoutId) window.clearTimeout(timeoutId);
      timeoutId = window.setTimeout(() => setCalendarLocked(true), calendarPasscodeRules.idleTimeoutMinutes * 60_000);
    };
    const events = ['mousemove', 'keydown', 'click', 'touchstart'];
    events.forEach((eventName) => window.addEventListener(eventName, armTimer));
    armTimer();
    return () => {
      if (timeoutId) window.clearTimeout(timeoutId);
      events.forEach((eventName) => window.removeEventListener(eventName, armTimer));
    };
  }, [calendarLockingEnabled, calendarPasscodeRules.idleTimeoutMinutes, calendarPasscodeRules.lockOnIdle]);

  useEffect(() => {
    loadSchedulingHealth();
  }, [loadSchedulingHealth]);



  useEffect(() => {
    if (!providerOptions.length || !selectedProviders.length) return;
    const availableProviderIds = new Set(providerOptions.map((provider) => provider.id));
    const validSelected = selectedProviders.filter((providerId) => availableProviderIds.has(providerId));
    if (validSelected.length !== selectedProviders.length) {
      setSelectedProviders(validSelected);
    }
  }, [providerOptions, selectedProviders]);

  useEffect(() => {
    if (!providerColumnOrder.length || !providerOptions.length) return;
    setProviderOptions((current) => {
      const order = new Map(providerColumnOrder.map((id, index) => [id, index]));
      const sorted = [...current].sort((left, right) => (order.get(left.id) ?? Number.MAX_SAFE_INTEGER) - (order.get(right.id) ?? Number.MAX_SAFE_INTEGER));
      return sorted.map((provider) => provider.id).join('|') === current.map((provider) => provider.id).join('|') ? current : sorted;
    });
  }, [providerColumnOrder, providerOptions.length]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(APPOINTMENT_PROVIDER_FILTER_STORAGE_KEY, JSON.stringify(selectedProviders));
    } catch {
      // ignore storage write errors
    }
  }, [selectedProviders]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(APPOINTMENT_HOVER_PREVIEW_STORAGE_KEY, String(hoverPreviewEnabled));
    } catch {
      // ignore storage write errors
    }
  }, [hoverPreviewEnabled]);

  useEffect(() => {
    if (hoverPreviewEnabled) return;
    setHovered(null);
    clearHoverHideTimeout();
  }, [clearHoverHideTimeout, hoverPreviewEnabled]);

  useEffect(() => {
    setTableActionMenuId(null);
  }, [date, displayMode, filterStatus, modalOpen, selectedProviders, view]);

  useEffect(() => {
    return () => {
      clearHoverHideTimeout();
    };
  }, [clearHoverHideTimeout]);



  useEffect(() => {

    if (!pending.appointmentTypeId && appointmentTypes.length) {

      setPending((prev) => ({

        ...prev,

        appointmentTypeId: appointmentTypes[0].id,

        durationMinutes: prev.id ? prev.durationMinutes : appointmentTypes[0].durationMinutes,

        isTelemed: appointmentTypes[0].isTelemed,

        generateTelemedLink: appointmentTypes[0].isTelemed,

      }));

    }

  }, [appointmentTypes, pending.appointmentTypeId]);

  useEffect(() => {
    if (!pending.locationId && locationOptions.length) {
      setPending((prev) => ({
        ...prev,
        locationId: locationOptions.find((location) => location.isPrimary)?.id || locationOptions[0]?.id || '',
      }));
    }
  }, [locationOptions, pending.locationId]);



  const providerMap = useMemo(() => new Map(providerOptions.map((p) => [p.id, p])), [providerOptions]);
  const patientMap = useMemo(() => new Map(patientOptions.map((p) => [p.id, p])), [patientOptions]);



  const persistAppointmentUpdate = useCallback(

    async (id: string, body: Record<string, unknown>) => {

      try {

        setSaving(true);

        const token = await getToken();

        if (!token) return;

        const res = await fetch(`${API_BASE}/appointments/${id}`, {

          method: 'PUT',

          headers: {

            'Content-Type': 'application/json',

            Authorization: `Bearer ${token}`,

          },

          body: JSON.stringify(body),

        });

        if (!res.ok) throw new Error(`Update failed ${res.status}`);

        await loadAppointments();

      } catch (err: any) {

        setError(err.message || tr('Failed to update appointment'));

      } finally {

        setSaving(false);

      }

    },

    [getToken, loadAppointments, tr],

  );

  const rebookAppointment = useCallback(

    async (appt: Appointment) => {

      try {

        setSaving(true);

        const token = await getToken();

        if (!token) return;

        const originalStart = toDate(appt.start);

        const originalEnd = toDate(appt.end);

        const durationMs = Math.max(5 * 60000, originalEnd.getTime() - originalStart.getTime());

        const nextStart = new Date(originalStart);

        nextStart.setDate(nextStart.getDate() + 7);

        if (nextStart.getTime() < Date.now()) {

          nextStart.setTime(Date.now() + 60 * 60000);

        }

        const nextEnd = new Date(nextStart.getTime() + durationMs);

        const res = await fetch(`${API_BASE}/appointments/${appt.id}/rebook`, {

          method: 'POST',

          headers: {

            'Content-Type': 'application/json',

            Authorization: `Bearer ${token}`,

          },

          body: JSON.stringify({

            startTime: nextStart.toISOString(),

            endTime: nextEnd.toISOString(),

            providerId: appt.providerId,

            appointmentTypeId: appt.appointmentType.id,

            billingServiceId: appt.billingService?.id ?? null,

            locationId: appt.locationId ?? appt.location?.id ?? null,

            note: rescheduleAuditNote || tr('Rebooked from calendar'),

          }),

        });

        if (!res.ok) throw new Error(`Rebook failed ${res.status}`);

        await loadAppointments();

      } catch (err: any) {

        setError(err.message || tr('Failed to rebook appointment'));

      } finally {

        setSaving(false);

      }

    },

    [getToken, loadAppointments, rescheduleAuditNote, tr],

  );

  const createAppointmentLetter = useCallback(

    async (appt: Appointment) => {

      try {

        setSaving(true);

        const token = await getToken();

        if (!token) return;

        const start = toDate(appt.start);

        const contentHtml = [
          '<p>Dear client,</p>',
          `<p>This letter relates to your appointment for <strong>${appt.appointmentType.name}</strong> with ${appt.provider} on ${start.toLocaleString(locale, { dateStyle: 'medium', timeStyle: 'short' })}.</p>`,
          '<p>Please contact the clinic if any appointment details need to be changed.</p>',
          `<p style="color:#64748b;font-size:12px">Appointment ID: ${appt.id}</p>`,
        ].join('');

        const res = await fetch(`${API_BASE}/patients/${appt.patientId}/notes`, {

          method: 'POST',

          headers: {

            'Content-Type': 'application/json',

            Authorization: `Bearer ${token}`,

          },

          body: JSON.stringify({

            title: `Appointment letter - ${appt.appointmentType.name}`,

            contentHtml,

            pinned: false,

          }),

        });

        if (!res.ok) throw new Error(`Letter creation failed ${res.status}`);

        const created = await res.json().catch(() => null);

        const query = new URLSearchParams({ tab: 'Notes', appointmentId: appt.id });

        if (created?.id) query.set('noteId', created.id);

        navigate(`/patients/${appt.patientId}?${query.toString()}`);

      } catch (err: any) {

        setError(err.message || tr('Failed to create appointment letter'));

      } finally {

        setSaving(false);

      }

    },

    [getToken, locale, navigate, tr],

  );

  const openVideoAiNotes = useCallback(

    async (appt: Appointment) => {

      if (appt.encounterId) {

        navigate(`/visits/${appt.encounterId}`);

        return;

      }

      await startOrOpenVisit(appt);

    },

    [navigate, startOrOpenVisit],

  );



  const filtered = useMemo(() => {

    return appointments.filter((a) => {

      const providerPool = selectedProviders.length ? selectedProviders : providerOptions.map((p) => p.id);

      const matchProvider = !providerPool.length || providerPool.includes(a.providerId);

      const matchStatus = filterStatus === 'all' || a.status === filterStatus;
      const matchVisibility = showCancelledAppointments || !['CANCELLED', 'NO_SHOW'].includes(a.status);
      const matchService =
        !selectedServices.length ||
        selectedServices.includes(a.appointmentType?.id || '') ||
        selectedServices.includes(a.billingService?.id || '');
      const matchLocation =
        !selectedLocations.length ||
        selectedLocations.includes(a.locationId || a.location?.id || '');

      const sameMonth =

        view === 'month'

          ? toDate(a.start).getMonth() === date.getMonth() &&

          toDate(a.start).getFullYear() === date.getFullYear()

          : true;

      return matchProvider && matchStatus && matchVisibility && matchService && matchLocation && sameMonth;

    });

  }, [appointments, selectedProviders, filterStatus, showCancelledAppointments, selectedServices, selectedLocations, view, date, providerOptions]);

  const visibleProviders = providerOptions.filter((p) =>

    (selectedProviders.length ? selectedProviders : providerOptions.map((pr) => pr.id)).includes(p.id),

  );
  const allProvidersSelected = selectedProviders.length === 0;







  const dayCounts = useMemo(() => {

    const counts = new Map<string, number>();

    const target = date;

    filtered.forEach((a) => {

      if (!isSameDay(toDate(a.start), target)) return;

      counts.set(a.providerId, (counts.get(a.providerId) || 0) + 1);

    });

    return counts;

  }, [filtered, date]);



  const weekDates = useMemo(() => {

    const start = new Date(date);

    const day = start.getDay();

    const diff = start.getDate() - day + (day === 0 ? -6 : 1); // Monday start

    const monday = new Date(start.setDate(diff));

    return Array.from({ length: 7 }, (_, i) => {

      const d = new Date(monday);

      d.setDate(monday.getDate() + i);

      return d;

    });

  }, [date]);
  const calendarTitle = useMemo(() => {
    if (view === 'month') {
      return date.toLocaleDateString(locale, { month: 'long', year: 'numeric' });
    }
    if (view === 'week') {
      const start = weekDates[0];
      const end = weekDates[weekDates.length - 1];
      if (!start || !end) return '';
      return `${start.toLocaleDateString(locale, { month: 'long', day: 'numeric' })} - ${end.toLocaleDateString(locale, { month: 'long', day: 'numeric', year: 'numeric' })}`;
    }
    return date.toLocaleDateString(locale, { weekday: 'long', month: 'long', day: 'numeric' });
  }, [date, locale, view, weekDates]);
  const calendarSubtitle = useMemo(() => {
    if (view === 'month') {
      const start = new Date(date.getFullYear(), date.getMonth(), 1);
      const end = new Date(date.getFullYear(), date.getMonth() + 1, 0);
      return `${start.toLocaleDateString(locale, { month: 'short', day: 'numeric', year: 'numeric' })} - ${end.toLocaleDateString(locale, { month: 'short', day: 'numeric', year: 'numeric' })}`;
    }
    if (view === 'week') return tr('Provider schedule and drag-to-reschedule');
    return tr('Clinical appointments and availability');
  }, [date, locale, tr, view]);
  const selectedDateAppointments = useMemo(
    () => filtered.filter((appt) => isSameDay(toDate(appt.start), date)),
    [date, filtered],
  );
  const visibleBlockouts = useMemo(() => {
    const providerPool = selectedProviders.length ? selectedProviders : providerOptions.map((provider) => provider.id);
    return calendarBlockouts.filter((blockout) => {
      const matchProvider = !providerPool.length || blockout.providerIds.some((providerId) => providerPool.includes(providerId));
      const sameMonth = view === 'month'
        ? toDate(blockout.startTime).getMonth() === date.getMonth() && toDate(blockout.startTime).getFullYear() === date.getFullYear()
        : true;
      return matchProvider && sameMonth;
    });
  }, [calendarBlockouts, date, providerOptions, selectedProviders, view]);
  const selectedDateBlockouts = useMemo(
    () => visibleBlockouts.filter((blockout) => isSameDay(toDate(blockout.startTime), date)),
    [date, visibleBlockouts],
  );
  const tableAppointments = useMemo(
    () =>
      [...filtered].sort((left, right) => {
        const startDiff = toDate(left.start).getTime() - toDate(right.start).getTime();
        if (startDiff !== 0) return startDiff;
        return left.patient.localeCompare(right.patient, locale);
      }),
    [filtered, locale],
  );
  const appointmentTableGridTemplate = useMemo(
    () => appointmentTableColumnOrder.map((columnId) => appointmentTableColumnWidths[columnId]).join(' '),
    [appointmentTableColumnOrder],
  );
  const appointmentTableColumnLabels = useMemo<Record<AppointmentTableColumnId, string>>(
    () => ({
      time: tr(view === 'day' ? 'Time' : 'Date / time'),
      patient: tr('Patient'),
      provider: tr('Provider'),
      service: tr('Service'),
      status: tr('Status'),
      actions: tr('Actions'),
    }),
    [tr, view],
  );
  function moveAppointmentTableColumn(columnId: AppointmentTableColumnId, direction: -1 | 1) {
    setAppointmentTableColumnOrder((current) => {
      const index = current.indexOf(columnId);
      const nextIndex = index + direction;
      if (index < 0 || nextIndex < 0 || nextIndex >= current.length) return current;
      const next = [...current];
      [next[index], next[nextIndex]] = [next[nextIndex], next[index]];
      void saveSchedulingPatch({ calendarColumnOrder: { providerIds: providerOptions.map((provider) => provider.id), tableColumns: next } });
      return next;
    });
  }
  const clinicListRows = useMemo<ClinicListRow[]>(
    () =>
      selectedDateAppointments.map((appt) => ({
        time: `${toDate(appt.start).toLocaleTimeString(locale, { hour: 'numeric', minute: '2-digit' })} - ${toDate(appt.end).toLocaleTimeString(locale, { hour: 'numeric', minute: '2-digit' })}`,
        patient: appt.patient,
        provider: appt.provider,
        service: appt.appointmentType?.name || appt.title || tr('Appointment'),
        status: tr(statusLabels[appt.status]),
        location: appt.location?.name || tr('No location'),
      })),
    [locale, selectedDateAppointments, tr],
  );
  const appointmentRequests = useMemo(
    () =>
      appointments
        .filter((appt) => appt.source === 'PORTAL' || appt.status === 'PORTAL_BOOKED')
        .sort((left, right) => toDate(left.start).getTime() - toDate(right.start).getTime())
        .slice(0, 5),
    [appointments],
  );
  const monthPickerDays = useMemo(() => {
    const start = startOfMiniCalendarMonth(date);
    return Array.from({ length: 42 }, (_, index) => {
      const current = new Date(start);
      current.setDate(start.getDate() + index);
      return current;
    });
  }, [date]);

  const activeClinicListRows = generatedClinicList?.rows ?? clinicListRows;

  function buildClinicListCsv(rows: ClinicListRow[]) {
    const header = [
      'Time',
      'Patient',
      'Provider',
      clinicListSettings.includeService ? 'Service' : null,
      clinicListSettings.includeStatus ? 'Status' : null,
      clinicListSettings.includeLocation ? 'Location' : null,
    ].filter(Boolean) as string[];
    const dataRows = rows.map((row) => [
      row.time,
      row.patient,
      row.provider,
      clinicListSettings.includeService ? row.service : null,
      clinicListSettings.includeStatus ? row.status : null,
      clinicListSettings.includeLocation ? row.location : null,
    ].filter((cell): cell is string => cell !== null));
    return [header, ...dataRows]
      .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(','))
      .join('\n');
  }

  function createClinicList() {
    setGeneratedClinicList({ generatedAt: new Date().toISOString(), rows: clinicListRows });
    setClinicListOpen(true);
  }

  function exportClinicList() {
    const rows = activeClinicListRows;
    const csv = buildClinicListCsv(rows);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `elen1-clinic-list-${date.toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(url);
  }

  function printClinicList() {
    window.print();
  }

  function emailClinicList() {
    const recipient = window.prompt(tr('Email clinic list to'), '');
    if (!recipient) return;
    const rows = activeClinicListRows;
    const body = encodeURIComponent(buildClinicListCsv(rows));
    const subject = encodeURIComponent(`Elen1 clinic list ${date.toISOString().slice(0, 10)}`);
    window.location.href = `mailto:${encodeURIComponent(recipient)}?subject=${subject}&body=${body}`;
  }

  function toggleLocationSelection(locationId: string) {
    setSelectedLocations((current) => {
      const next = current.includes(locationId) ? current.filter((id) => id !== locationId) : [...current, locationId];
      void saveSchedulingPatch({ calendarFilters: { staffIds: selectedProviders, serviceIds: selectedServices, locationIds: next, multiLocationEnabled: true } });
      return next;
    });
  }

  function toggleServiceSelection(serviceId: string) {
    setSelectedServices((current) => {
      const next = current.includes(serviceId) ? current.filter((id) => id !== serviceId) : [...current, serviceId];
      void saveSchedulingPatch({ calendarFilters: { staffIds: selectedProviders, serviceIds: next, locationIds: selectedLocations, multiLocationEnabled: true } });
      return next;
    });
  }

  function moveProviderColumn(providerId: string, direction: -1 | 1) {
    setProviderOptions((current) => {
      const index = current.findIndex((provider) => provider.id === providerId);
      const nextIndex = index + direction;
      if (index < 0 || nextIndex < 0 || nextIndex >= current.length) return current;
      const next = [...current];
      const [item] = next.splice(index, 1);
      if (!item) return current;
      next.splice(nextIndex, 0, item);
      setProviderColumnOrder(next.map((provider) => provider.id));
      void saveSchedulingPatch({ calendarColumnOrder: { providerIds: next.map((provider) => provider.id), tableColumns: appointmentTableColumnOrder } });
      return next;
    });
  }



  function onDragStart(ev: React.DragEvent, appt: Appointment) {

    ev.dataTransfer.setData('application/neoemr-appt', appt.id);

  }

  function onBlockoutDragStart(ev: React.DragEvent, blockout: CalendarBlockout) {
    ev.dataTransfer.setData('application/elen1-blockout', blockout.id);
  }



  function onDropSlot(targetDate: Date, providerId?: string) {

    return (ev: React.DragEvent) => {

      ev.preventDefault();
      if (calendarLocked) {
        setError(tr('Calendar is locked. Unlock it before creating or moving appointments.'));
        return;
      }

      const id = ev.dataTransfer.getData('application/neoemr-appt');
      const blockoutId = ev.dataTransfer.getData('application/elen1-blockout');

      if (blockoutId) {
        const blockout = calendarBlockouts.find((item) => item.id === blockoutId);
        if (!blockout) return;
        const duration = toDate(blockout.endTime).getTime() - toDate(blockout.startTime).getTime();
        const newStart = new Date(targetDate);
        const newEnd = new Date(newStart.getTime() + Math.max(5 * 60000, duration));
        const nextProviderIds = providerId ? Array.from(new Set([providerId, ...blockout.providerIds.filter((id) => id !== providerId)])) : blockout.providerIds;
        void saveCalendarBlockouts(calendarBlockouts.map((item) => item.id === blockoutId ? {
          ...item,
          startTime: newStart.toISOString(),
          endTime: newEnd.toISOString(),
          providerIds: nextProviderIds,
          updatedAt: new Date().toISOString(),
        } : item));
        return;
      }

      if (!id) return;

      const appt = appointments.find((a) => a.id === id);

      if (!appt) return;

      const duration = toDate(appt.end).getTime() - toDate(appt.start).getTime();

      const newStart = new Date(targetDate);

      const newEnd = new Date(newStart.getTime() + duration);

      const newProviderId = providerId ?? appt.providerId;

      setAppointments((prev) =>

        prev.map((a) => {

          if (a.id !== id) return a;

          return {

            ...a,

            start: newStart.toISOString(),

            end: newEnd.toISOString(),

            providerId: newProviderId,

            provider: providerMap.get(newProviderId)?.name ?? a.provider,
            providerAvatarUrl: providerMap.get(newProviderId)?.avatarUrl ?? a.providerAvatarUrl ?? null,

          };

        }),

      );

      persistAppointmentUpdate(id, {

        startTime: newStart.toISOString(),

        endTime: newEnd.toISOString(),

        providerId: newProviderId,

      });

    };

  }



  function onAllowDrop(ev: React.DragEvent) {

    ev.preventDefault();

  }



  function openModalAt(dt: Date, providerId?: string) {
    if (calendarLocked) {
      setError(tr('Calendar is locked. Unlock it before creating or moving appointments.'));
      return;
    }

    const defaultProviderId = providerId ?? providerOptions[0]?.id ?? '';

    const defaultTypeId = appointmentTypes[0]?.id || '';
    const defaultLocationId = locationOptions.find((location) => location.isPrimary)?.id || locationOptions[0]?.id || '';

    const defaultDuration =

      appointmentTypes.find((t) => t.id === defaultTypeId)?.durationMinutes ?? defaultSlotMinutes;

    const defaultIsTelemed = appointmentTypes.find((t) => t.id === defaultTypeId)?.isTelemed ?? false;



    setPending({

      id: undefined,

      patientId: '',

      providerId: defaultProviderId,

      status: 'SCHEDULED',

      appointmentTypeId: defaultTypeId,
      billingServiceId: serviceVariantsEnabled
        ? appointmentTypes.find((t) => t.id === defaultTypeId)?.serviceVariants?.find((variant) => variant.active !== false)?.id || ''
        : '',
      locationId: defaultLocationId,

      start: toLocalInput(dt),

      durationMinutes: defaultDuration,

      title: '',

      isTelemed: defaultIsTelemed,

      generateTelemedLink: defaultIsTelemed,

      joinUrl: null,
      roomResourceId: '',
      assetResourceIds: [],
      additionalStaffIds: [],
      medicalFormTemplateIds: [],
      medicalForms: [],
      emailReminderEnabled: false,
      smsReminderEnabled: false,
      reminderMinutesBefore: 1440,
      multiServiceIds: [],
      packageBookingEnabled: false,
      packageId: '',
      packageQuantity: 1,
      classBookingEnabled: false,
      classParticipantIds: [],
      classParticipantStatuses: {},
      classCapacity: 8,
      classNotificationsEnabled: true,
      repeatEnabled: false,
      repeatFrequency: 'weekly',
      repeatCount: 1,
      bookingDepositEnabled: false,
      bookingDepositAmount: 0,
      paymentHoldEnabled: false,
      paymentHoldReference: '',

    });

    setModalOpen(true);

  }



  async function submitNewAppointment(appointmentData: any) {

    // We use appointmentData passed from the modal instead of 'pending' state

    // but since we are updating 'pending' state via the modal's initialData prop (indirectly)

    // or rather, the modal calls onSubmit with the *new* pending state.

    // Wait, the modal calls onSubmit with the *local* pending state of the modal.

    // So we should accept an argument here.



    // Actually, looking at the modal code: onSubmit(pending).

    // So this function needs to accept the appointment object.



    const apptToSave = appointmentData || pending; // Fallback to pending if not passed (though it should be)



    if (!apptToSave.patientId || !apptToSave.providerId || !apptToSave.appointmentTypeId) {

      setError(tr('Patient, provider, and appointment type are required'));

      return;

    }

    setSaving(true);

    setError(null);

    try {

      const token = await getToken();

      if (!token) return;

      const startDate = new Date(apptToSave.start);

      const endDate = new Date(startDate.getTime() + apptToSave.durationMinutes * 60000);

      const selectedAppointmentType = appointmentTypes.find((t) => t.id === apptToSave.appointmentTypeId);
      const selectedVirtualMeta = virtualMetaForAppointmentType(selectedAppointmentType);
      const selectedClassMeta = classMetaForAppointmentType(selectedAppointmentType);
      const selectedTimingMeta = timingMetaForAppointmentType(selectedAppointmentType);
      const isTelemedVisit =
        Boolean(apptToSave.isTelemed) ||
        Boolean(selectedAppointmentType?.isTelemed) ||
        selectedVirtualMeta.virtualDeliveryMode !== 'in_person';



      const additionalStaffNames = Array.isArray(apptToSave.additionalStaffIds)
        ? apptToSave.additionalStaffIds
            .map((staffId: string) => providerOptions.find((provider) => provider.id === staffId)?.name)
            .filter(Boolean)
        : [];
      const classParticipantNames = Array.isArray(apptToSave.classParticipantIds)
        ? apptToSave.classParticipantIds
            .map((patientId: string) => patientOptions.find((patient) => patient.id === patientId)?.name)
            .filter(Boolean)
        : [];
      const classParticipantStatusLines = Array.isArray(apptToSave.classParticipantIds)
        ? apptToSave.classParticipantIds
            .map((patientId: string) => {
              const patientName = patientOptions.find((patient) => patient.id === patientId)?.name;
              const status = apptToSave.classParticipantStatuses?.[patientId] || 'SCHEDULED';
              return patientName ? `${patientName}: ${status}` : null;
            })
            .filter(Boolean)
        : [];

      const bookingNotes = [
        apptToSave.packageBookingEnabled
          ? `Package appointment booking${apptToSave.packageId ? `: ${apptToSave.packageId}` : ''}${apptToSave.packageQuantity ? ` (${apptToSave.packageQuantity} session${Number(apptToSave.packageQuantity) === 1 ? '' : 's'})` : ''}`
          : null,
        additionalStaffNames.length ? `Additional staff: ${additionalStaffNames.join(', ')}` : null,
        selectedClassMeta.isClass || apptToSave.classBookingEnabled
          ? [
              `Class booking: ${1 + classParticipantNames.length}/${apptToSave.classCapacity || selectedClassMeta.classCapacity} participants`,
              classParticipantNames.length ? `Class participants: ${classParticipantNames.join(', ')}` : null,
              classParticipantStatusLines.length ? `Class participant statuses: ${classParticipantStatusLines.join('; ')}` : null,
              apptToSave.classNotificationsEnabled === false ? 'Class notifications disabled.' : 'Class notifications enabled.',
              selectedClassMeta.classPortalVisible ? 'Client portal class visible.' : null,
            ].filter(Boolean).join('\n')
          : null,
        isTelemedVisit
          ? [
              `Virtual delivery: ${selectedVirtualMeta.virtualDeliveryMode}`,
              selectedVirtualMeta.groupVideoEnabled ? `Group video enabled: max ${selectedVirtualMeta.maxVideoParticipants} participants` : null,
              selectedVirtualMeta.videoProviderOverride ? `Video provider: ${selectedVirtualMeta.videoProviderOverride}` : null,
            ].filter(Boolean).join('\n')
          : null,
      ].filter(Boolean).join('\n');

      const payload: Record<string, unknown> = {

        patientId: apptToSave.patientId,

        providerId: apptToSave.providerId,
        locationId: apptToSave.locationId,

        status: apptToSave.status,

        appointmentTypeId: apptToSave.appointmentTypeId,

        billingServiceId: serviceVariantsEnabled ? apptToSave.billingServiceId || undefined : undefined,

        startTime: startDate.toISOString(),

        endTime: endDate.toISOString(),

        title: apptToSave.title || undefined,

        isTelemed: isTelemedVisit || undefined,
        roomResourceId: apptToSave.roomResourceId || undefined,
        assetResourceIds: Array.isArray(apptToSave.assetResourceIds) ? apptToSave.assetResourceIds : [],
        medicalFormTemplateIds: Array.isArray(apptToSave.medicalFormTemplateIds) ? apptToSave.medicalFormTemplateIds : [],
        emailReminderEnabled: Boolean(apptToSave.emailReminderEnabled),
        smsReminderEnabled: Boolean(apptToSave.smsReminderEnabled),
        reminderMinutesBefore:
          Number.isFinite(Number(apptToSave.reminderMinutesBefore)) && Number(apptToSave.reminderMinutesBefore) > 0
            ? Number(apptToSave.reminderMinutesBefore)
            : null,
        notes: bookingNotes || undefined,
        bufferBeforeMinutes: selectedTimingMeta.bufferBeforeMinutes,
        bufferAfterMinutes: selectedTimingMeta.bufferAfterMinutes,
        packageId: apptToSave.packageBookingEnabled ? apptToSave.packageId || undefined : undefined,
        packageQuantity: apptToSave.packageBookingEnabled ? Math.max(1, Number(apptToSave.packageQuantity || 1)) : undefined,

      };



      if (apptToSave.id) {

        await persistAppointmentUpdate(apptToSave.id, payload);

        setModalOpen(false);

        return;

      }



      const serviceTypeIds = Array.from(new Set([
        apptToSave.appointmentTypeId,
        ...(Array.isArray(apptToSave.multiServiceIds) ? apptToSave.multiServiceIds : []),
      ].filter(Boolean)));
      const repeatCount = apptToSave.repeatEnabled ? Math.min(Math.max(Number(apptToSave.repeatCount) || 1, 1), 24) : 1;
      const createdAppointments: any[] = [];

      for (let occurrenceIndex = 0; occurrenceIndex < repeatCount; occurrenceIndex += 1) {
        let occurrenceStart = new Date(startDate);
        if (occurrenceIndex > 0) {
          if (apptToSave.repeatFrequency === 'daily') occurrenceStart.setDate(occurrenceStart.getDate() + occurrenceIndex);
          else if (apptToSave.repeatFrequency === 'monthly') occurrenceStart.setMonth(occurrenceStart.getMonth() + occurrenceIndex);
          else occurrenceStart.setDate(occurrenceStart.getDate() + occurrenceIndex * 7);
        }

        for (const [serviceIndex, typeId] of serviceTypeIds.entries()) {
          const type = appointmentTypes.find((item) => item.id === typeId);
          const timingMeta = timingMetaForAppointmentType(type);
          const durationMinutes = serviceIndex === 0 ? apptToSave.durationMinutes : timingMeta.effectiveDurationMinutes || type?.durationMinutes || apptToSave.durationMinutes;
          const serviceStart = new Date(occurrenceStart);
          if (serviceIndex > 0) {
            const priorMinutes = serviceTypeIds.slice(0, serviceIndex).reduce((sum, priorTypeId, priorIndex) => {
              const priorType = appointmentTypes.find((item) => item.id === priorTypeId);
              const priorTimingMeta = timingMetaForAppointmentType(priorType);
              return sum + (priorIndex === 0 ? apptToSave.durationMinutes : priorTimingMeta.effectiveDurationMinutes || priorType?.durationMinutes || apptToSave.durationMinutes);
            }, 0);
            serviceStart.setMinutes(serviceStart.getMinutes() + priorMinutes);
          }
          const serviceEnd = new Date(serviceStart.getTime() + durationMinutes * 60000);
          const nextPayload = {
            ...payload,
            appointmentTypeId: typeId,
            billingServiceId: serviceIndex === 0 ? payload.billingServiceId : undefined,
            startTime: serviceStart.toISOString(),
            endTime: serviceEnd.toISOString(),
            bufferBeforeMinutes: timingMeta.bufferBeforeMinutes,
            bufferAfterMinutes: timingMeta.bufferAfterMinutes,
            title: serviceTypeIds.length > 1
              ? `${apptToSave.title || type?.name || 'Appointment'} (${serviceIndex + 1}/${serviceTypeIds.length})`
              : payload.title,
          };

          const res = await fetch(`${API_BASE}/appointments`, {

            method: 'POST',

            headers: {

              'Content-Type': 'application/json',

              ...(token ? { Authorization: `Bearer ${token}` } : {}),

            },

            body: JSON.stringify(nextPayload),

          });

          if (!res.ok) throw new Error(`Create failed ${res.status}`);

          const created = await res.json().catch(() => null);
          createdAppointments.push(created);
        }
      }
      const created = createdAppointments[0];

      if (created?.id && (apptToSave.bookingDepositEnabled || apptToSave.paymentHoldEnabled)) {
        const depositAmount = Math.max(0, Number(apptToSave.bookingDepositAmount || 0));
        const paymentStatus = apptToSave.paymentHoldEnabled ? 'PAYMENT_HOLD' : 'BOOKING_DEPOSIT';
        const paymentTotal = paymentStatus === 'BOOKING_DEPOSIT' ? depositAmount : 0;
        const paymentRes = await fetch(`${API_BASE}/appointments/${created.id}/payment-registration`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            paymentStatus,
            expectedAmount: paymentStatus === 'BOOKING_DEPOSIT' ? depositAmount : paymentTotal,
            discount: 0,
            tax: 0,
            finalAmount: paymentStatus === 'BOOKING_DEPOSIT' ? depositAmount : paymentTotal,
            paidAmount: paymentStatus === 'BOOKING_DEPOSIT' ? depositAmount : 0,
            paymentHoldReference: apptToSave.paymentHoldReference || undefined,
            externalReceiptReference: apptToSave.paymentHoldReference || undefined,
            notes: paymentStatus === 'BOOKING_DEPOSIT'
              ? 'Booking deposit processed during calendar booking.'
              : 'Payment hold placed during calendar booking.',
          }),
        });
        if (!paymentRes.ok) throw new Error(`Payment registration failed ${paymentRes.status}`);
      }



      // Generate telemed session immediately if requested and we have an id

      if (isTelemedVisit && apptToSave.generateTelemedLink !== false && selectedVirtualMeta.autoCreateVideoSession !== false && created?.id) {

        try {

          await createTelemedSession(created.id);

        } catch (err: any) {

          // Surface error but still continue

          setError(err.message || tr('Telemed session creation failed'));

        }

      }



      await loadAppointments();

      setModalOpen(false);

    } catch (err: any) {

      setError(err.message || tr('Failed to save appointment'));

    } finally {

      setSaving(false);

    }

  }



  function editAppointment(appt: Appointment) {

    const start = toDate(appt.start);

    const end = toDate(appt.end);

    const duration = (end.getTime() - start.getTime()) / 60000;

    setPending({

      id: appt.id,
      encounterId: appt.encounterId ?? null,

      patientId: appt.patientId,

      providerId: appt.providerId,

      status: appt.status,

      appointmentTypeId: appt.appointmentType.id,
      billingServiceId: serviceVariantsEnabled ? appt.billingService?.id ?? '' : '',
      locationId: appt.locationId || locationOptions.find((location) => location.isPrimary)?.id || locationOptions[0]?.id || '',

      start: toLocalInput(start),

      durationMinutes: duration,

      title: appt.title || '',

      isTelemed: Boolean(appt.isTelemed || appt.appointmentType.isTelemed),

      generateTelemedLink: Boolean(appt.isTelemed || appt.appointmentType.isTelemed),

      joinUrl: appt.joinUrl || null,
      roomResourceId: appt.roomResourceId ?? '',
      assetResourceIds: appt.assetResourceIds ?? [],
      additionalStaffIds: appt.additionalStaffIds ?? [],
      medicalFormTemplateIds: appt.medicalForms?.map((item) => item.templateId) ?? [],
      medicalForms: appt.medicalForms ?? [],
      emailReminderEnabled: Boolean(appt.emailReminderEnabled),
      smsReminderEnabled: Boolean(appt.smsReminderEnabled),
      reminderMinutesBefore: appt.reminderMinutesBefore ?? 1440,
      multiServiceIds: [],
      packageBookingEnabled: false,
      packageId: '',
      packageQuantity: 1,
      classBookingEnabled: false,
      classParticipantIds: [],
      classParticipantStatuses: {},
      classCapacity: 8,
      classNotificationsEnabled: true,
      repeatEnabled: false,
      repeatFrequency: 'weekly',
      repeatCount: 1,
      bookingDepositEnabled: false,
      bookingDepositAmount: 0,
      paymentHoldEnabled: false,
      paymentHoldReference: '',

    });

    setModalOpen(true);

  }



  function changeDay(offset: number) {

    const next = new Date(date);

    if (view === 'week') {

      next.setDate(date.getDate() + offset * 7);

    } else if (view === 'month') {

      next.setMonth(date.getMonth() + offset);

    } else {

      next.setDate(date.getDate() + offset);

    }

    setDate(next);

  }

  function toggleProviderSelection(providerId: string) {

    setSelectedProviders((prev) => {

      const allSelected = prev.length === 0;

      if (allSelected) {

        const normalized = providerOptions.map((provider) => provider.id).filter((id) => id !== providerId);
        void saveSchedulingPatch({ calendarFilters: { staffIds: normalized, serviceIds: selectedServices, locationIds: selectedLocations, multiLocationEnabled: true } });
        return normalized;

      }

      const next = prev.includes(providerId) ? prev.filter((id) => id !== providerId) : [...prev, providerId];

      const normalized = next.length === providerOptions.length ? [] : next;
      void saveSchedulingPatch({ calendarFilters: { staffIds: normalized, serviceIds: selectedServices, locationIds: selectedLocations, multiLocationEnabled: true } });
      return normalized;

    });

  }



  function MonthGrid() {

    const first = new Date(date.getFullYear(), date.getMonth(), 1);

    const startDay = (first.getDay() + 6) % 7;

    const daysInMonth = new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();

    const cells = Array.from({ length: startDay + daysInMonth }, (_, i) => {

      const dayNum = i - startDay + 1;

      return dayNum > 0 ? dayNum : null;

    });



    return (

      <div className="grid grid-cols-7 gap-[1px] bg-slate-100 rounded-xl overflow-hidden border border-slate-100 min-h-[calc(100vh-260px)]">

        {Array.from({ length: 7 }, (_, i) => {
          const mondayFirstRef = new Date(Date.UTC(2024, 0, 1 + i));
          return mondayFirstRef.toLocaleDateString(locale, { weekday: 'short' });
        }).map((d) => (

          <div key={d} className="text-center text-xs text-slate-500 uppercase bg-white py-2">

            {d}

          </div>

        ))}

        {cells.map((day, idx) => {

          const dayDate = day

            ? new Date(date.getFullYear(), date.getMonth(), day)

            : new Date(date);

          const dayAppts = filtered.filter(

            (a) =>

              toDate(a.start).getDate() === day &&

              toDate(a.start).getMonth() === date.getMonth() &&

              toDate(a.start).getFullYear() === date.getFullYear(),

          );
          const dayBlockouts = visibleBlockouts.filter((blockout) =>
            toDate(blockout.startTime).getDate() === day &&
            toDate(blockout.startTime).getMonth() === date.getMonth() &&
            toDate(blockout.startTime).getFullYear() === date.getFullYear(),
          );

          return (

            <div

              key={idx}

              className={cn(

                'p-3 min-h-[110px] relative cursor-pointer hover:shadow-md transition bg-white',

                day ? '' : 'bg-transparent shadow-none border-0',

              )}

              onClick={(e) => {

                if (!day) return;

                // Default to 9am for month view using slot length

                const start = new Date(dayDate);

                start.setHours(9, 0, 0, 0);

                openModalAt(start);

              }}

            >

              {day && (

                <>

                  <div className="flex items-center justify-between text-sm">

                    <span className="font-semibold">{day}</span>

                    <span className="text-xs text-slate-500">
                      {dayAppts.length} {tr('appt')}{dayBlockouts.length ? ` · ${dayBlockouts.length} ${tr('blocked')}` : ''}
                    </span>

                  </div>

                  <div className="mt-2 flex flex-wrap gap-1.5">

                    {dayAppts.map((a) => {

                      const color = a.appointmentType.color || getTypeColor(a.appointmentType.id);
                      const patientAvatarUrl = a.patientAvatarUrl ?? patientMap.get(a.patientId)?.avatarUrl ?? null;
                      const patientInitials = a.patient
                        .split(' ')
                        .filter(Boolean)
                        .map((part) => part[0])
                        .join('')
                        .slice(0, 2)
                        .toUpperCase();

                      return (

                        <div

                          key={a.id}

                          className="inline-flex items-center gap-1.5 text-[11px] bg-slate-50 border border-slate-200 rounded-full px-2 py-0.5"

                        >

                          <span className="h-3 w-1.5 rounded-full shrink-0" style={{ backgroundColor: color }} />
                          {patientAvatarUrl ? (
                            <img src={patientAvatarUrl} alt={a.patient} className="h-4 w-4 rounded-full border border-slate-200 object-cover shrink-0" />
                          ) : (
                            <span className="h-4 w-4 rounded-full bg-slate-100 text-slate-600 text-[9px] font-semibold flex items-center justify-center shrink-0">
                              {patientInitials || 'PT'}
                            </span>
                          )}

                          <span className="font-medium truncate">{a.patient}</span>

                        </div>

                      );

                    })}

                    {dayBlockouts.map((blockout) => (
                      <button
                        key={blockout.id}
                        type="button"
                        onClick={(event) => {
                          event.stopPropagation();
                          editBlockout(blockout);
                        }}
                        className="inline-flex items-center gap-1.5 rounded-full border border-dashed border-slate-300 bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-600"
                      >
                        <span className="h-2 w-2 rounded-full bg-slate-400" />
                        <span className="truncate">{blockout.title}</span>
                      </button>
                    ))}

                  </div>

                </>

              )}

            </div>

          );

        })}

      </div>

    );

  }



  function DayGrid() {

    const slotMinutes = defaultSlotMinutes || 30;

    const slotHeight = 78;
    const providerHeaderHeight = 44;

    const dayStartHour = calendarConfiguration.visibleHoursStart;

    const dayEndHour = calendarConfiguration.visibleHoursEnd;

    const slots: Date[] = [];

    const start = new Date(date);

    start.setHours(dayStartHour, 0, 0, 0);

    const end = new Date(date);

    end.setHours(dayEndHour, 0, 0, 0);

    for (let t = start.getTime(); t < end.getTime(); t += slotMinutes * 60000) {

      slots.push(new Date(t));

    }



    const baseStart = slots[0];

    const totalHeight = slots.length * slotHeight;



    const handleCreateAt = (y: number, providerId?: string) => {

      const minutesFromStart = Math.floor(y / slotHeight) * slotMinutes;

      const target = new Date(baseStart.getTime() + minutesFromStart * 60000);

      openModalAt(target, providerId);

    };



    const onDropColumn = (providerId?: string) => {

      return (ev: React.DragEvent<HTMLDivElement>) => {

        ev.preventDefault();

        const rect = ev.currentTarget.getBoundingClientRect();

        const offsetY = ev.clientY - rect.top - providerHeaderHeight;

        if (offsetY < 0) return;

        const minutesFromStart = Math.floor(offsetY / slotHeight) * slotMinutes;

        const target = new Date(baseStart.getTime() + minutesFromStart * 60000);

        onDropSlot(target, providerId)(ev);

      };

    };



    return (

      <div className="border border-slate-100 rounded-[24px] overflow-x-auto overflow-y-hidden min-h-[calc(100vh-180px)] bg-white">

          <div

            className="grid gap-0"

            style={{

              gridTemplateColumns: `120px repeat(${visibleProviders.length || 1}, minmax(220px, 1fr))`,

            }}

          >

            <div className="bg-white border-r border-b border-slate-100 sticky left-0 z-20" style={{ gridRow: `span ${slots.length + 1}` }}>
              <div className="px-3 py-3 bg-slate-50 text-xs font-semibold text-slate-500 border-b border-slate-100 sticky top-0 z-30">
                {tr('Time')}
              </div>
              <div className="relative bg-white" style={{ minHeight: totalHeight }}>
                {slots.map((slot, idx) => {
                  const slotLabel = slot.toLocaleTimeString(locale, { hour: 'numeric', minute: '2-digit' });
                  return (
                    <div
                      key={idx}
                      className="absolute left-0 right-0 text-xs text-slate-500 px-3 flex items-start"
                      style={{ top: idx * slotHeight, height: slotHeight }}
                    >
                      <span className="mt-1">{slotLabel}</span>
                    </div>
                  );
                })}
              </div>
            </div>

            {visibleProviders.map((p) => {
              const providerInitials = p.name
                .split(' ')
                .filter(Boolean)
                .map((part) => part[0])
                .join('')
                .slice(0, 2)
                .toUpperCase();

              const pAppts = filtered.filter(

                (a) => a.providerId === p.id && isSameDay(toDate(a.start), slots[0] || date),

              );
              const pBlockouts = visibleBlockouts.filter(
                (blockout) => blockout.providerIds.includes(p.id) && isSameDay(toDate(blockout.startTime), slots[0] || date),
              );

              const layout = (() => {
                const sorted = [...pAppts].sort(
                  (a, b) => toDate(a.start).getTime() - toDate(b.start).getTime() || toDate(a.end).getTime() - toDate(b.end).getTime(),
                );
                const cols: number[] = [];
                const placed: Array<{ appt: Appointment; col: number; top: number; height: number }> = [];

                sorted.forEach((a) => {
                  const durationMinutes = Math.max(
                    slotMinutes,
                    Math.round((toDate(a.end).getTime() - toDate(a.start).getTime()) / 60000),
                  );
                  const startMinutes = Math.max(
                    0,
                    Math.round((toDate(a.start).getTime() - (baseStart?.getTime() ?? 0)) / 60000),
                  );
                  const top = (startMinutes / slotMinutes) * slotHeight;
                  const height = Math.max(slotHeight, (durationMinutes / slotMinutes) * slotHeight);

                  let colIdx = 0;
                  for (; colIdx < cols.length; colIdx++) {
                    if (top >= cols[colIdx]) break;
                  }
                  cols[colIdx] = top + height;
                  placed.push({ appt: a, col: colIdx, top, height });
                });

                const colCount = placed.length ? Math.max(...placed.map((p) => p.col + 1)) : 1;
                return { placed, colCount };
              })();

              return (

                <div

                  key={p.id}

                  className="relative border-t border-l border-slate-100 bg-white"

                  style={{ minHeight: totalHeight + providerHeaderHeight }}

                  onClick={(e) => {

                    const rect = (e.currentTarget as HTMLDivElement).getBoundingClientRect();

                    const offsetY = e.clientY - rect.top - providerHeaderHeight;

                    if (offsetY < 0) return;

                    handleCreateAt(offsetY, p.id);

                  }}

                  onDragOver={onAllowDrop}

                  onDrop={onDropColumn(p.id)}

                >

                  <div className="sticky top-0 z-30 backdrop-blur-sm border-b border-slate-100 flex items-center gap-2 px-3 py-2">
                    {calendarConfiguration.showProviderAvatars && p.avatarUrl ? (
                      <img src={p.avatarUrl} alt={p.name} className="h-6 w-6 rounded-full object-cover border border-slate-200" />
                    ) : (
                      <div className="h-6 w-6 rounded-full bg-slate-100 text-slate-600 text-[10px] font-semibold flex items-center justify-center">
                        {providerInitials || 'PR'}
                      </div>
                    )}
                    <span className="text-sm font-semibold text-ink truncate">{p.name}</span>
                    <span className="ml-auto inline-flex items-center gap-1 text-[11px] font-medium text-slate-500 bg-white border border-slate-200 rounded-full px-2 py-0.5">
                      <CalendarDays size={12} className="text-slate-400" />
                      {dayCounts.get(p.id) || 0}
                    </span>
                  </div>

                  <div className="absolute inset-0 pointer-events-none" style={{ top: providerHeaderHeight }}>

                    {slots.map((_, idx) => (

                      <div

                        key={idx}

                        className="absolute left-0 right-0 border-t border-slate-100"

                        style={{ top: idx * slotHeight }}

                      />

                    ))}

                  </div>

                  <div className="absolute inset-0" style={{ top: providerHeaderHeight }}>

                    {pBlockouts.map((blockout) => {
                      const blockStart = toDate(blockout.startTime);
                      const blockEnd = toDate(blockout.endTime);
                      const startMinutes = Math.max(0, Math.round((blockStart.getTime() - (baseStart?.getTime() ?? 0)) / 60000));
                      const durationMinutes = Math.max(slotMinutes, Math.round((blockEnd.getTime() - blockStart.getTime()) / 60000));
                      const top = (startMinutes / slotMinutes) * slotHeight;
                      const height = Math.max(slotHeight, (durationMinutes / slotMinutes) * slotHeight);
                      return (
                        <div
                          key={`${p.id}-${blockout.id}`}
                          draggable
                          onDragStart={(event) => onBlockoutDragStart(event, blockout)}
                          onClick={(event) => {
                            event.stopPropagation();
                            editBlockout(blockout);
                          }}
                          className="absolute left-2 right-2 z-10 cursor-grab rounded-xl border border-dashed border-slate-300 bg-slate-100/95 p-2 text-xs text-slate-600 shadow-sm transition hover:border-slate-400 hover:bg-slate-200"
                          style={{ top, height }}
                        >
                          <div className="flex items-center justify-between gap-2">
                            <span className="truncate font-semibold text-slate-800">{blockout.title}</span>
                            {blockout.seriesId ? <span className="shrink-0 rounded-full bg-white px-1.5 py-0.5 text-[10px] font-semibold text-slate-500">{tr('Repeats')}</span> : null}
                          </div>
                          <p className="mt-0.5 truncate">
                            {blockStart.toLocaleTimeString(locale, { hour: 'numeric', minute: '2-digit' })} - {blockEnd.toLocaleTimeString(locale, { hour: 'numeric', minute: '2-digit' })}
                          </p>
                          {blockout.reason ? <p className="mt-0.5 truncate text-slate-500">{blockout.reason}</p> : null}
                        </div>
                      );
                    })}

                    {layout.placed.map(({ appt: a, col, top, height }) => {
                      const widthPct = 100 / layout.colCount;
                      const leftPct = widthPct * col;
                      return (

                        <div

                          key={a.id}

                          className="absolute"

                          style={{
                            top,
                            height,
                            left: `calc(${leftPct}% + 4px)`,
                            width: `calc(${widthPct}% - 8px)`,
                          }}

                        >

                          <ApptBlock

                            appt={a}
                            patientAvatarUrl={a.patientAvatarUrl ?? patientMap.get(a.patientId)?.avatarUrl ?? null}

                            compact={false}

                            staticLayout

                            heightPx={height}
                            allowClientCardAccess={appointmentSettingsConfig.allowClientCardAccess}

                            color={a.appointmentType.color || getTypeColor(a.appointmentType.id)}

                            onDragStart={onDragStart}

                            onHover={openHoverPreview}

                            onLeave={scheduleHoverPreviewClose}

                            onClick={() => editAppointment(a)}

                          />

                        </div>

                      );

                    })}

                  </div>

                </div>

              );

            })}

          </div>

      </div>

    );

  }

  function ResourceGrid() {

    const resources = [
      ...roomOptions.map((resource) => ({ ...resource, group: tr('Rooms') })),
      ...assetOptions.map((resource) => ({ ...resource, group: tr('Equipment') })),
      { id: 'unassigned', name: tr('Unassigned'), type: tr('Resource'), locationId: null, group: tr('Other') },
    ];

    const resourceAppointments = (resourceId: string) =>
      filtered
        .filter((appt) => {
          if (!isSameDay(toDate(appt.start), date)) return false;
          if (resourceId === 'unassigned') return !appt.roomResourceId && !(appt.assetResourceIds?.length);
          return appt.roomResourceId === resourceId || Boolean(appt.assetResourceIds?.includes(resourceId));
        })
        .sort((left, right) => toDate(left.start).getTime() - toDate(right.start).getTime());

    return (
      <div className="overflow-x-auto rounded-[24px] border border-slate-100 bg-white">
        <div className="grid min-w-[980px] gap-0" style={{ gridTemplateColumns: `repeat(${Math.max(resources.length, 1)}, minmax(240px, 1fr))` }}>
          {resources.map((resource) => {
            const rows = resourceAppointments(resource.id);
            return (
              <div key={resource.id} className="min-h-[calc(100vh-260px)] border-l border-slate-100 first:border-l-0">
                <div className="sticky top-0 z-20 border-b border-slate-100 bg-slate-50 px-3 py-3">
                  <p className="truncate text-sm font-semibold text-ink">{resource.name}</p>
                  <p className="text-xs text-slate-500">{resource.group} · {rows.length} {tr('appointments')}</p>
                </div>
                <div className="space-y-2 p-3">
                  {!rows.length ? (
                    <button
                      type="button"
                      onClick={() => openModalAt(date)}
                      className="w-full rounded-xl border border-dashed border-slate-200 px-3 py-8 text-sm font-medium text-slate-400 hover:bg-slate-50"
                    >
                      {tr('No bookings')}
                    </button>
                  ) : (
                    rows.map((appt) => (
                      <button
                        key={`${resource.id}-${appt.id}`}
                        type="button"
                        onClick={() => editAppointment(appt)}
                        className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-left shadow-sm transition hover:border-sky-200 hover:bg-sky-50/40"
                      >
                        <div className="flex items-center justify-between gap-2">
                          <span className="truncate text-sm font-semibold text-slate-900">{appt.patient}</span>
                          <span className={cn('rounded-full px-2 py-0.5 text-[10px] font-semibold', statusBadgeClasses[appt.status])}>{tr(statusLabels[appt.status])}</span>
                        </div>
                        <p className="mt-1 text-xs text-slate-500">
                          {toDate(appt.start).toLocaleTimeString(locale, { hour: 'numeric', minute: '2-digit' })} - {toDate(appt.end).toLocaleTimeString(locale, { hour: 'numeric', minute: '2-digit' })}
                        </p>
                        <p className="mt-1 truncate text-xs text-slate-600">{appt.appointmentType.name} · {appt.provider}</p>
                      </button>
                    ))
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );

  }

  function WeekGrid() {

    const slotMinutes = defaultSlotMinutes || 30;

    const dayStartHour = calendarConfiguration.visibleHoursStart;

    const dayEndHour = calendarConfiguration.visibleHoursEnd;

    const weekSlots: Array<{ label: string; start: Date; end: Date }> = [];

    const base = new Date(date);

    base.setHours(dayStartHour, 0, 0, 0);

    const endBase = new Date(date);

    endBase.setHours(dayEndHour, 0, 0, 0);

    for (let t = base.getTime(); t < endBase.getTime(); t += slotMinutes * 60000) {

      const start = new Date(t);

      const end = new Date(t + slotMinutes * 60000);

      weekSlots.push({ label: start.toLocaleTimeString(locale, { hour: 'numeric', minute: '2-digit' }), start, end });

    }



    return (

      <div className="overflow-x-auto rounded-[24px] border border-slate-100 bg-white">
        <div

          className="grid min-w-[980px] gap-0 min-h-[calc(100vh-260px)] bg-white"

          style={{ gridTemplateColumns: `110px repeat(7, minmax(0, 1fr))` }}

        >

          <div className="w-[110px]" />

          {weekDates.map((d) => (

            <div key={d.toDateString()} className="text-sm font-semibold px-3 py-3 bg-slate-50 border-l border-slate-100">

              {d.toLocaleDateString(locale, { weekday: 'short', month: 'short', day: 'numeric' })}

            </div>

          ))}

          {weekSlots.map((slot, idx) => (

            <div key={idx} className="contents">

              <div className="text-xs text-slate-500 px-3 py-4 bg-white border-t border-slate-100 w-[110px]">

                {slot.label}

              </div>

              {weekDates.map((d) => {

                const slotDate = new Date(d);

                slotDate.setHours(slot.start.getHours(), slot.start.getMinutes(), 0, 0);

                const slotDateEnd = new Date(slotDate);

                slotDateEnd.setMinutes(slotDate.getMinutes() + slotMinutes);

                const slotAppts = filtered.filter((a) => {

                  const start = toDate(a.start);

                  return (

                    start.getDate() === slotDate.getDate() &&

                    start.getMonth() === slotDate.getMonth() &&

                    start.getFullYear() === slotDate.getFullYear() &&

                    start >= slotDate &&

                    start < slotDateEnd

                  );

                });
                const slotBlockouts = visibleBlockouts.filter((blockout) => {
                  const start = toDate(blockout.startTime);
                  return (
                    start.getDate() === slotDate.getDate() &&
                    start.getMonth() === slotDate.getMonth() &&
                    start.getFullYear() === slotDate.getFullYear() &&
                    start >= slotDate &&
                    start < slotDateEnd
                  );
                });

                return (

                  <div

                    key={d.toDateString() + idx}

                    className="border-t border-l border-slate-100 bg-white px-1 py-1 min-h-[32px]"

                    onClick={() => openModalAt(slotDate)}

                    onDragOver={onAllowDrop}

                    onDrop={onDropSlot(slotDate)}

                  >

                    <div className="flex flex-col gap-1">

                      {slotBlockouts.map((blockout) => (
                        <button
                          key={blockout.id}
                          type="button"
                          draggable
                          onDragStart={(event) => onBlockoutDragStart(event, blockout)}
                          onClick={(event) => {
                            event.stopPropagation();
                            editBlockout(blockout);
                          }}
                          className="rounded-lg border border-dashed border-slate-300 bg-slate-100 px-2 py-1 text-left text-[11px] font-semibold text-slate-600 hover:bg-slate-200"
                        >
                          {blockout.title}
                        </button>
                      ))}

                      {slotAppts.map((a) => {

                        const startStr = toDate(a.start).toLocaleTimeString(locale, { hour: 'numeric', minute: '2-digit' });

                        const endStr = toDate(a.end).toLocaleTimeString(locale, { hour: 'numeric', minute: '2-digit' });

                        const color = a.appointmentType.color || getTypeColor(a.appointmentType.id);
                        const patientAvatarUrl = a.patientAvatarUrl ?? patientMap.get(a.patientId)?.avatarUrl ?? null;
                        const patientInitials = a.patient
                          .split(' ')
                          .filter(Boolean)
                          .map((part) => part[0])
                          .join('')
                          .slice(0, 2)
                          .toUpperCase();

                        return (

                          <div

                            key={a.id}

                            draggable

                            onDragStart={(e) => onDragStart(e, a)}

                            onMouseEnter={(event) => openHoverPreview(a, event.currentTarget.getBoundingClientRect())}

                            onMouseLeave={scheduleHoverPreviewClose}

                            onClick={(e) => {

                              e.stopPropagation();

                              editAppointment(a);

                            }}

                            className="flex items-center gap-2 text-xs leading-tight bg-slate-50 border border-slate-200 rounded-md px-2 py-1 hover:bg-slate-100 cursor-move"

                          >

                            <span className="h-3 w-1.5 rounded-full" style={{ backgroundColor: color }} />
                            {patientAvatarUrl ? (
                              <img src={patientAvatarUrl} alt={a.patient} className="h-4 w-4 rounded-full border border-slate-200 object-cover shrink-0" />
                            ) : (
                              <span className="h-4 w-4 rounded-full bg-slate-100 text-slate-600 text-[9px] font-semibold flex items-center justify-center shrink-0">
                                {patientInitials || 'PT'}
                              </span>
                            )}

                            <span className="font-medium truncate">{a.patient}</span>

                            <span

                              className={cn(

                                'inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide whitespace-nowrap',

                                statusBadgeClasses[a.status],

                              )}

                            >

                              {tr(statusLabels[a.status])}

                            </span>

                            {a.isTelemed || a.appointmentType.isTelemed ? (

                              <span className="inline-flex items-center gap-1 text-[10px] text-sky-700 bg-sky-50 border border-sky-100 rounded-full px-2 py-0.5">

                                <Video size={10} /> {tr('Telemed')}

                              </span>

                            ) : null}

                            <span className="text-slate-500 text-[11px] whitespace-nowrap">

                              {startStr} - {endStr}

                            </span>

                          </div>

                        );

                      })}

                    </div>

                  </div>

                );

              })}

            </div>

          ))}

        </div>
      </div>

    );

  }



  return (

    <div className="appointments-board h-full min-h-0 bg-white">

      <div className="grid h-full min-h-0 grid-cols-1 xl:grid-cols-[320px_minmax(0,1fr)]">
        <aside
          className={cn(
            'appointments-scroll min-h-0 flex-col border-b border-slate-200 bg-white p-4 xl:h-full xl:overflow-y-auto xl:border-b-0 xl:border-r',
            showCompactTools ? 'flex' : 'hidden xl:flex',
          )}
        >
          <div className="flex items-start justify-between gap-3 border-b border-slate-100 pb-4">
            <div>
              <div className="flex items-center gap-2 text-slate-400">
                <PanelLeft size={15} />
                <span className="text-xs font-semibold uppercase tracking-[0.18em]">{tr('Schedule tools')}</span>
              </div>
              <h2 className="mt-1 text-lg font-semibold text-ink">{calendarTitle}</h2>
              <p className="text-sm text-slate-500">{calendarSubtitle}</p>
            </div>
            <button
              className="appt-highlight inline-flex items-center gap-2 rounded-xl bg-lime-400 px-3 py-2 text-sm font-semibold text-slate-900 shadow-soft hover:bg-lime-300"
              onClick={() => openModalAt(new Date())}
            >
              <Plus size={15} /> {tr('New')}
            </button>
            <button
              type="button"
              onClick={() => setShowCompactTools(false)}
              className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-500 transition hover:bg-slate-50 xl:hidden"
              aria-label={tr('Close')}
            >
              <X size={16} />
            </button>
          </div>

          <div className="mt-4 space-y-4">
            <div className="rounded-2xl bg-slate-50 p-3">
              <div className="flex items-center justify-between">
                <button
                  className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-600 transition hover:bg-slate-100"
                  onClick={() => changeDay(-1)}
                  aria-label={tr('Prev')}
                >
                  <ChevronLeft size={16} />
                </button>
                <div className="text-center">
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">
                    {date.toLocaleDateString(locale, { month: 'long' })}
                  </p>
                  <p className="text-sm font-semibold text-ink">
                    {date.toLocaleDateString(locale, { year: 'numeric' })}
                  </p>
                </div>
                <button
                  className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-600 transition hover:bg-slate-100"
                  onClick={() => changeDay(1)}
                  aria-label={tr('Next')}
                >
                  <ChevronRight size={16} />
                </button>
              </div>

              <div className="mt-3 grid grid-cols-3 gap-2">
                <button className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-medium text-ink" onClick={() => setDate(new Date())}>
                  {tr('Today')}
                </button>
                <button
                  className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-medium text-slate-600"
                  onClick={() => {
                    const next = new Date();
                    next.setDate(next.getDate() + 1);
                    setDate(next);
                  }}
                >
                  {tr('Tomorrow')}
                </button>
                <button
                  className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-medium text-slate-600"
                  onClick={() => setView('week')}
                >
                  {tr('This week')}
                </button>
              </div>

              <div className="mt-3 grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => void saveCurrentCalendarPreset()}
                  className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-medium text-slate-600 hover:bg-slate-50"
                >
                  {savedPreset ? tr('Preset saved') : tr('Save preset')}
                </button>
                <button
                  type="button"
                  onClick={toggleCalendarLock}
                  className={cn(
                    'rounded-xl border px-3 py-2 text-xs font-medium',
                    calendarLocked ? 'border-amber-200 bg-amber-50 text-amber-700' : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50',
                  )}
                >
                  {calendarLocked ? tr('Unlock calendar') : tr('Lock calendar')}
                </button>
              </div>

              <div className="mt-4">
                <div className="mb-2 grid grid-cols-7 text-center text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                  {Array.from({ length: 7 }, (_, i) => {
                    const mondayFirstRef = new Date(Date.UTC(2024, 0, 1 + i));
                    return (
                      <span key={i}>
                        {mondayFirstRef.toLocaleDateString(locale, { weekday: 'narrow' })}
                      </span>
                    );
                  })}
                </div>
                <div className="grid grid-cols-7 gap-1">
                  {monthPickerDays.map((day) => {
                    const isCurrentMonth = day.getMonth() === date.getMonth();
                    const isActiveDay = isSameDay(day, date);
                    const hasAppointments = appointments.some((appt) => isSameDay(toDate(appt.start), day));
                    return (
                      <button
                        key={day.toISOString()}
                        type="button"
                        onClick={() => setDate(new Date(day))}
                        className={cn(
                          'relative h-9 rounded-lg text-xs font-medium transition',
                          isActiveDay
                            ? 'bg-ink text-white shadow-sm'
                            : isCurrentMonth
                              ? 'text-slate-700 hover:bg-white'
                              : 'text-slate-300 hover:bg-white',
                        )}
                      >
                        {day.getDate()}
                        {hasAppointments ? (
                          <span
                            className={cn(
                              'absolute bottom-1 left-1/2 h-1.5 w-1.5 -translate-x-1/2 rounded-full',
                              isActiveDay ? 'bg-white' : 'bg-sky-500',
                            )}
                          />
                        ) : null}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-3">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-sm font-semibold text-ink">{tr('Blockouts')}</p>
                  <p className="text-xs text-slate-500">{selectedDateBlockouts.length} {tr('unavailable time slots')}</p>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    const start = new Date(date);
                    start.setHours(9, 0, 0, 0);
                    openBlockoutDialogAt(start);
                  }}
                  className="rounded-lg border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                >
                  {tr('Add')}
                </button>
              </div>
              <div className="mt-3 space-y-2">
                {!selectedDateBlockouts.length ? (
                  <p className="rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-500">{tr('No blockouts for this day.')}</p>
                ) : (
                  selectedDateBlockouts.slice(0, 5).map((blockout) => (
                    <button
                      key={`side-blockout-${blockout.id}`}
                      type="button"
                      onClick={() => editBlockout(blockout)}
                      className="w-full rounded-lg border border-dashed border-slate-300 bg-slate-50 px-3 py-2 text-left text-xs hover:bg-slate-100"
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="truncate font-semibold text-slate-800">{blockout.title}</span>
                        {blockout.seriesId ? <span className="rounded-full bg-white px-2 py-0.5 text-[10px] font-semibold text-slate-500">{tr('Series')}</span> : null}
                      </div>
                      <p className="mt-1 text-slate-500">
                        {toDate(blockout.startTime).toLocaleTimeString(locale, { hour: 'numeric', minute: '2-digit' })} - {toDate(blockout.endTime).toLocaleTimeString(locale, { hour: 'numeric', minute: '2-digit' })}
                      </p>
                    </button>
                  ))
                )}
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-3">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-sm font-semibold text-ink">{tr('Booking wizard')}</p>
                  <p className="text-xs text-slate-500">{tr('Multi-service, package, and quick-client booking')}</p>
                </div>
                <button
                  type="button"
                  onClick={() => setShowBookingWizard(true)}
                  className="rounded-lg bg-sky-600 px-2.5 py-1.5 text-xs font-semibold text-white hover:bg-sky-700"
                >
                  {tr('Open')}
                </button>
              </div>
              <div className="mt-3 grid grid-cols-3 gap-2 text-center text-xs">
                <div className="rounded-lg bg-slate-50 px-2 py-2">
                  <p className="font-semibold text-ink">{wizardSelectedServices.length || 1}</p>
                  <p className="text-slate-500">{tr('services')}</p>
                </div>
                <div className="rounded-lg bg-slate-50 px-2 py-2">
                  <p className="font-semibold text-ink">{wizardClientMode === 'quick-create' ? tr('New') : tr('Existing')}</p>
                  <p className="text-slate-500">{tr('client')}</p>
                </div>
                <div className="rounded-lg bg-slate-50 px-2 py-2">
                  <p className="font-semibold text-ink">{wizardPackageMode ? tr('On') : tr('Off')}</p>
                  <p className="text-slate-500">{tr('package')}</p>
                </div>
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-3">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-sm font-semibold text-ink">{tr('Calendar settings')}</p>
                  <p className="text-xs text-slate-500">{tr('Appearance, overlap, legend, and day notes')}</p>
                </div>
                <button
                  type="button"
                  onClick={() => setShowCalendarSettings((current) => !current)}
                  className="rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50"
                >
                  {showCalendarSettings ? tr('Hide') : tr('Configure')}
                </button>
              </div>
              {showCalendarSettings ? (
                <div className="mt-3 space-y-3">
                  <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500">
                    {tr('Density')}
                    <select
                      value={calendarDensity}
                      onChange={(event) => {
                        const density = event.target.value as 'comfortable' | 'compact';
                        setCalendarDensity(density);
                        void saveSchedulingPatch({ calendarAppearance: { density, showCancelledAppointments, showAppointmentLegend: showLegend, colorMode: 'service' } });
                      }}
                      className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium normal-case tracking-normal text-slate-700"
                    >
                      <option value="comfortable">{tr('Comfortable')}</option>
                      <option value="compact">{tr('Compact')}</option>
                    </select>
                  </label>
                  <label className="flex items-start gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-600">
                    <input type="checkbox" className="mt-1" checked={appointmentOverlapEnabled} onChange={(event) => {
                      setAppointmentOverlapEnabled(event.target.checked);
                      void saveSchedulingPatch({ appointmentOverlapEnabled: event.target.checked });
                    }} />
                    <span>
                      <span className="block font-semibold text-slate-700">{tr('Enable appointment overlap')}</span>
                      <span className="block text-xs text-slate-500">{tr('Allow appointments to be placed in overlapping time slots.')}</span>
                    </span>
                  </label>
                  <label className="flex items-start gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-600">
                    <input type="checkbox" className="mt-1" checked={showCancelledAppointments} onChange={(event) => {
                      setShowCancelledAppointments(event.target.checked);
                      void saveSchedulingPatch({ calendarAppearance: { density: calendarDensity, showCancelledAppointments: event.target.checked, showAppointmentLegend: showLegend, colorMode: 'service' } });
                    }} />
                    <span>
                      <span className="block font-semibold text-slate-700">{tr('Show cancelled appointments')}</span>
                      <span className="block text-xs text-slate-500">{tr('Keep cancelled and no-show records visible in the schedule.')}</span>
                    </span>
                  </label>
                  <button
                    type="button"
                    onClick={() => {
                      const next = !showLegend;
                      setShowLegend(next);
                      void saveSchedulingPatch({ calendarAppearance: { density: calendarDensity, showCancelledAppointments, showAppointmentLegend: next, colorMode: 'service' } });
                    }}
                    className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                  >
                    {showLegend ? tr('Hide appointment legend') : tr('Show appointment legend')}
                  </button>
                  <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
                    {tr('Calendar passcode rules')}: {calendarPasscodeRules.minLength}+ {tr('characters')}
                    {calendarPasscodeRules.requireNumbers ? `, ${tr('numbers required')}` : ''}
                    {calendarPasscodeRules.lockOnIdle ? `, ${tr('idle lock')} ${calendarPasscodeRules.idleTimeoutMinutes} ${tr('min')}` : ''}
                  </div>
                </div>
              ) : null}
              {showLegend ? (
                <div className="mt-3 rounded-lg bg-slate-50 p-3">
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{tr('Appointment icon legend')}</p>
                  <div className="mt-2 grid gap-2 text-xs text-slate-600">
                    {appointmentIconLegend.filter((item) => item.enabled !== false).map((item) => (
                      <LegendRow key={item.id} color={item.color} label={tr(item.label)} />
                    ))}
                  </div>
                </div>
              ) : null}
              {calendarPresets.length ? (
                <div className="mt-3 rounded-lg border border-slate-100 bg-white p-3">
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{tr('Calendar presets')}</p>
                  <div className="mt-2 grid gap-2">
                    {calendarPresets.slice(0, 4).map((preset) => (
                      <button key={preset.id} type="button" onClick={() => applyCalendarPreset(preset)} className="rounded-lg border border-slate-200 px-3 py-2 text-left text-xs font-medium text-slate-700 hover:bg-slate-50">
                        {preset.name}
                      </button>
                    ))}
                  </div>
                </div>
              ) : null}
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-3">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-sm font-semibold text-ink">{tr('Calendar activities')}</p>
                  <p className="text-xs text-slate-500">{tr('Tasks, meetings, reminders, and follow-ups inside the calendar')}</p>
                </div>
                <span className="rounded-full bg-slate-100 px-2 py-1 text-[10px] font-semibold uppercase text-slate-500">{calendarActivities.length}</span>
              </div>
              <div className="mt-3 space-y-2">
                {calendarActivities.slice(0, 4).map((activity) => (
                  <div key={activity.id} className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-2 text-xs">
                    <p className="font-semibold text-slate-800">{tr(activity.title)}</p>
                    <p className="mt-0.5 text-slate-500">{activity.date} · {tr(activity.type)} · {activity.owner ? tr(activity.owner) : tr('Unassigned')}</p>
                  </div>
                ))}
                {!calendarActivities.length ? (
                  <p className="rounded-lg border border-dashed border-slate-200 px-3 py-2 text-xs text-slate-500">{tr('No calendar activities configured.')}</p>
                ) : null}
              </div>
              {shiftNotes.length ? (
                <div className="mt-3 rounded-lg border border-slate-100 bg-slate-50 p-3">
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{tr('Shift notes')}</p>
                  <div className="mt-2 space-y-2">
                    {shiftNotes.filter((note) => note.date === selectedDateKey).slice(0, 3).map((note) => (
                      <p key={note.id} className="rounded-lg bg-white px-3 py-2 text-xs text-slate-600">
                        <strong>{note.providerName ? tr(note.providerName) : tr('Shift')}</strong>: {note.note}
                      </p>
                    ))}
                    {!shiftNotes.some((note) => note.date === selectedDateKey) ? (
                      <p className="text-xs text-slate-500">{tr('No shift notes for this day.')}</p>
                    ) : null}
                  </div>
                </div>
              ) : null}
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-3">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-sm font-semibold text-ink">{tr('Day note')}</p>
                  <p className="text-xs text-slate-500">{date.toLocaleDateString(locale, { weekday: 'long', month: 'short', day: 'numeric' })}</p>
                </div>
                <span className="rounded-full bg-slate-100 px-2 py-1 text-[10px] font-semibold uppercase text-slate-500">{tr('Internal')}</span>
              </div>
              <textarea
                value={dayNote}
                onChange={(event) => setDayNote(event.target.value)}
                onBlur={() => void saveDayNote()}
                rows={4}
                placeholder={tr('Add shift notes, clinic notices, staffing reminders, or room alerts for this day.')}
                className="mt-3 w-full resize-none rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 outline-none focus:border-sky-300"
              />
              {dayNote ? (
                <p className="mt-2 text-xs text-emerald-700">{tr('Day note saves when the field loses focus.')}</p>
              ) : null}
            </div>

            {calendarConfiguration.showLocationFilter ? <div className="rounded-2xl bg-slate-50/60 p-3">
              <div className="flex items-center justify-between">
                <p className="text-sm font-semibold text-ink">{tr('Locations')}</p>
                <span className="text-xs text-slate-500">
                  {selectedLocations.length ? `${selectedLocations.length}/${locationOptions.length}` : tr('All')}
                </span>
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                <button
                  type="button"
                  className={cn(
                    'inline-flex min-w-0 items-center gap-2 rounded-full border px-2.5 py-1.5 text-left text-xs transition',
                    !selectedLocations.length ? 'border-ink bg-ink text-white' : 'border-slate-200 text-slate-700 hover:bg-slate-50',
                  )}
                  onClick={() => {
                    setSelectedLocations([]);
                    void saveSchedulingPatch({ calendarFilters: { staffIds: selectedProviders, serviceIds: selectedServices, locationIds: [], multiLocationEnabled: true } });
                  }}
                >
                  <span className="font-medium">{tr('All locations')}</span>
                </button>
                {locationOptions.map((location) => {
                  const active = selectedLocations.includes(location.id);
                  const count = filtered.filter((appt) => (appt.locationId || appt.location?.id) === location.id).length;
                  return (
                    <button
                      key={location.id}
                      type="button"
                      className={cn(
                        'inline-flex min-w-0 max-w-full items-center gap-2 rounded-full border px-2.5 py-1.5 text-left text-xs transition',
                        active ? 'border-slate-900 bg-slate-900 text-white' : 'border-slate-200 text-slate-700 hover:bg-slate-50',
                      )}
                      onClick={() => toggleLocationSelection(location.id)}
                    >
                      <span className="truncate font-medium">{location.name}</span>
                      <span className={cn('rounded-full px-1.5 py-0.5 text-[10px] font-semibold', active ? 'bg-white/15 text-white/90' : 'bg-slate-200/80 text-slate-500')}>
                        {count}
                      </span>
                    </button>
                  );
                })}
                {!locationOptions.length ? <span className="text-xs text-slate-500">{tr('No locations')}</span> : null}
              </div>
            </div> : null}

            <div className="rounded-2xl bg-slate-50/60 p-3">
              <div className="flex items-center justify-between">
                <p className="text-sm font-semibold text-ink">{tr('Services')}</p>
                <span className="text-xs text-slate-500">{selectedServices.length ? `${selectedServices.length}/${appointmentTypes.length}` : tr('All')}</span>
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                <button
                  type="button"
                  className={cn('inline-flex min-w-0 items-center gap-2 rounded-full border px-2.5 py-1.5 text-left text-xs transition', !selectedServices.length ? 'border-ink bg-ink text-white' : 'border-slate-200 text-slate-700 hover:bg-slate-50')}
                  onClick={() => {
                    setSelectedServices([]);
                    void saveSchedulingPatch({ calendarFilters: { staffIds: selectedProviders, serviceIds: [], locationIds: selectedLocations, multiLocationEnabled: true } });
                  }}
                >
                  <span className="font-medium">{tr('All services')}</span>
                </button>
                {appointmentTypes.slice(0, 10).map((service) => {
                  const active = selectedServices.includes(service.id);
                  return (
                    <button
                      key={service.id}
                      type="button"
                      className={cn('inline-flex min-w-0 max-w-full items-center gap-2 rounded-full border px-2.5 py-1.5 text-left text-xs transition', active ? 'border-slate-900 bg-slate-900 text-white' : 'border-slate-200 text-slate-700 hover:bg-slate-50')}
                      onClick={() => toggleServiceSelection(service.id)}
                    >
                      <span className="truncate font-medium">{tr(service.name)}</span>
                    </button>
                  );
                })}
                {!appointmentTypes.length ? <span className="text-xs text-slate-500">{tr('No services')}</span> : null}
              </div>
            </div>

            {clinicListSettings.enabled ? <div className="rounded-2xl border border-slate-200 bg-white p-3">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-sm font-semibold text-ink">{tr('Clinic List')}</p>
                  <p className="text-xs text-slate-500">
                    {activeClinicListRows.length} {tr('appointments today')}
                    {generatedClinicList ? ` · ${tr('Generated')} ${new Date(generatedClinicList.generatedAt).toLocaleTimeString(locale, { hour: 'numeric', minute: '2-digit' })}` : ''}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setClinicListOpen((current) => !current)}
                  className="rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50"
                >
                  {clinicListOpen ? tr('Hide') : tr('View')}
                </button>
              </div>
              <div className="mt-3 grid grid-cols-2 gap-2">
                <button type="button" onClick={createClinicList} className="inline-flex items-center justify-center gap-1 rounded-lg border border-slate-200 px-2 py-2 text-xs font-medium text-slate-600 hover:bg-slate-50">
                  <Rows3 size={13} /> {tr('Create')}
                </button>
                <button type="button" onClick={printClinicList} className="inline-flex items-center justify-center gap-1 rounded-lg border border-slate-200 px-2 py-2 text-xs font-medium text-slate-600 hover:bg-slate-50">
                  <Printer size={13} /> {tr('Print')}
                </button>
                <button type="button" onClick={exportClinicList} className="inline-flex items-center justify-center gap-1 rounded-lg border border-slate-200 px-2 py-2 text-xs font-medium text-slate-600 hover:bg-slate-50">
                  <Download size={13} /> {tr('CSV')}
                </button>
                <button type="button" onClick={emailClinicList} className="inline-flex items-center justify-center gap-1 rounded-lg border border-slate-200 px-2 py-2 text-xs font-medium text-slate-600 hover:bg-slate-50">
                  <Mail size={13} /> {tr('Email')}
                </button>
              </div>
              {clinicListOpen ? (
                <div className="mt-3 max-h-64 space-y-2 overflow-y-auto">
                  {!activeClinicListRows.length ? (
                    <p className="rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-500">{tr('No appointments in this range.')}</p>
                  ) : (
                    activeClinicListRows.map((row, index) => (
                      <div key={`${row.time}-${row.patient}-${index}`} className="rounded-lg bg-slate-50 px-3 py-2 text-xs">
                        <div className="flex items-center justify-between gap-2">
                          <span className="font-semibold text-ink">{row.time}</span>
                          {clinicListSettings.includeStatus ? <span className="text-slate-500">{row.status}</span> : null}
                        </div>
                        <p className="mt-1 font-medium text-slate-700">{row.patient}</p>
                        <p className="text-slate-500">{[clinicListSettings.includeService ? row.service : null, row.provider, clinicListSettings.includeLocation ? row.location : null].filter(Boolean).join(' · ')}</p>
                      </div>
                    ))
                  )}
                </div>
              ) : null}
            </div> : null}

            <div className="rounded-2xl border border-slate-200 bg-white p-3">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-sm font-semibold text-ink">{tr('Appointment requests')}</p>
                  <p className="text-xs text-slate-500">{appointmentRequests.length} {tr('online booking requests')}</p>
                </div>
                <span className="rounded-full bg-sky-50 px-2 py-1 text-[10px] font-semibold uppercase text-sky-700">{tr('Portal')}</span>
              </div>
              <div className="mt-3 space-y-2">
                {!appointmentRequests.length ? (
                  <p className="rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-500">{tr('No appointment requests pending.')}</p>
                ) : (
                  appointmentRequests.map((appt) => (
                    <div key={`request-${appt.id}`} className="rounded-lg border border-slate-200 px-3 py-2 text-xs">
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-semibold text-ink">{appt.patient}</span>
                        <span className={cn('rounded-full px-2 py-0.5 font-semibold', statusBadgeClasses[appt.status])}>{tr(statusLabels[appt.status])}</span>
                      </div>
                      <p className="mt-1 text-slate-500">
                        {toDate(appt.start).toLocaleDateString(locale, { month: 'short', day: 'numeric' })} · {toDate(appt.start).toLocaleTimeString(locale, { hour: 'numeric', minute: '2-digit' })}
                      </p>
                      <div className="mt-2 grid grid-cols-2 gap-2">
                        <button type="button" onClick={() => void runAppointmentAutomation(appt.id, 'confirm')} className="rounded-md border border-emerald-200 bg-emerald-50 px-2 py-1 font-semibold text-emerald-700">
                          {tr('Confirm')}
                        </button>
                        <button type="button" onClick={() => editAppointment(appt)} className="rounded-md border border-slate-200 px-2 py-1 font-semibold text-slate-700">
                          {tr('Review')}
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-3">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-sm font-semibold text-ink">{tr('Client notifications')}</p>
                  <p className="text-xs text-slate-500">{tr('Booking confirmations, reminders, and status messages')}</p>
                </div>
                <span className="rounded-full bg-sky-50 px-2 py-1 text-[10px] font-semibold uppercase text-sky-700">
                  {selectedDateAppointments.filter((appt) => appt.emailReminderEnabled || appt.smsReminderEnabled).length}
                </span>
              </div>
              <div className="mt-3 grid gap-2 text-xs">
                <div className="rounded-lg bg-slate-50 px-3 py-2 text-slate-600">
                  {tr('Email reminders')}: {selectedDateAppointments.filter((appt) => appt.emailReminderEnabled).length}
                </div>
                <div className="rounded-lg bg-slate-50 px-3 py-2 text-slate-600">
                  {tr('Text reminders')}: {selectedDateAppointments.filter((appt) => appt.smsReminderEnabled).length}
                </div>
                <button
                  type="button"
                  onClick={() => {
                    const next = selectedDateAppointments.find((appt) => !['CANCELLED', 'NO_SHOW', 'COMPLETED'].includes(appt.status));
                    if (next) void runAppointmentAutomation(next.id, 'schedule-reminders');
                  }}
                  disabled={!selectedDateAppointments.some((appt) => !['CANCELLED', 'NO_SHOW', 'COMPLETED'].includes(appt.status))}
                  className="rounded-lg border border-slate-200 px-3 py-2 text-left font-semibold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {tr('Schedule next client notification')}
                </button>
              </div>
            </div>

            <div className="rounded-2xl bg-slate-50/60 p-3">
              <div className="flex items-center justify-between">
                <p className="text-sm font-semibold text-ink">{tr('Doctors')}</p>
                <span className="text-xs text-slate-500">
                  {allProvidersSelected ? tr('All') : `${selectedProviders.length}/${providerOptions.length}`}
                </span>
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                <button
                  className={cn(
                    'inline-flex min-w-0 items-center gap-2 rounded-full border px-2.5 py-1.5 text-left text-xs transition',
                    allProvidersSelected ? 'border-ink bg-ink text-white' : 'border-slate-200 text-slate-700 hover:bg-slate-50',
                  )}
                  onClick={() => {
                    setSelectedProviders([]);
                    void saveSchedulingPatch({ calendarFilters: { staffIds: [], serviceIds: selectedServices, locationIds: selectedLocations, multiLocationEnabled: true } });
                  }}
                >
                  <span className="font-medium">{tr('All doctors')}</span>
                  <span className={cn('rounded-full px-1.5 py-0.5 text-[10px] font-semibold', allProvidersSelected ? 'bg-white/15 text-white/90' : 'bg-slate-200/80 text-slate-500')}>
                    {filtered.length}
                  </span>
                </button>
                {providerOptions.map((provider) => {
                  const active = allProvidersSelected || selectedProviders.includes(provider.id);
                  const providerAppointments = filtered.filter((appt) => appt.providerId === provider.id).length;
                  return (
                    <button
                      key={provider.id}
                      className={cn(
                        'inline-flex min-w-0 max-w-full items-center gap-2 rounded-full border px-2.5 py-1.5 text-left text-xs transition',
                        active ? 'border-slate-900 bg-slate-900 text-white' : 'border-slate-200 text-slate-700 hover:bg-slate-50',
                      )}
                      onClick={() => toggleProviderSelection(provider.id)}
                      title={`${provider.name} • ${dayCounts.get(provider.id) || 0} ${tr('today')}`}
                    >
                      <span className="truncate font-medium">{provider.name}</span>
                      <span className={cn('rounded-full px-1.5 py-0.5 text-[10px] font-semibold', active ? 'bg-white/15 text-white/90' : 'bg-slate-200/80 text-slate-500')}>
                        {providerAppointments}
                      </span>
                    </button>
                  );
                })}

                {!providerOptions.length && <span className="text-xs text-slate-500">{tr('No providers')}</span>}
              </div>
              {providerOptions.length > 1 ? (
                <div className="mt-4 rounded-xl border border-slate-200 bg-white p-3">
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{tr('Column ordering')}</p>
                  <div className="mt-2 space-y-2">
                    {providerOptions.map((provider, index) => (
                      <div key={`order-${provider.id}`} className="flex items-center justify-between gap-2 rounded-lg bg-slate-50 px-2 py-1.5">
                        <span className="truncate text-xs font-medium text-slate-700">{index + 1}. {provider.name}</span>
                        <span className="flex gap-1">
                          <button
                            type="button"
                            onClick={() => moveProviderColumn(provider.id, -1)}
                            disabled={index === 0}
                            className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-slate-200 bg-white text-slate-500 hover:bg-slate-50 disabled:opacity-30"
                            aria-label={tr('Move left')}
                          >
                            <ChevronLeft size={13} />
                          </button>
                          <button
                            type="button"
                            onClick={() => moveProviderColumn(provider.id, 1)}
                            disabled={index === providerOptions.length - 1}
                            className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-slate-200 bg-white text-slate-500 hover:bg-slate-50 disabled:opacity-30"
                            aria-label={tr('Move right')}
                          >
                            <ChevronRight size={13} />
                          </button>
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </aside>

        <section className="flex min-h-0 flex-col bg-white p-4 lg:p-5 xl:overflow-hidden">
          <div className="mb-4 flex items-center justify-between gap-3 xl:hidden">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">{tr('Calendar workspace')}</p>
              <p className="text-sm text-slate-500">{tr('Open scheduling tools only when you need filters, settings, and clinic controls.')}</p>
            </div>
            <button
              type="button"
              onClick={() => setShowCompactTools((current) => !current)}
              className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
              aria-expanded={showCompactTools}
            >
              <PanelLeft size={15} />
              {showCompactTools ? tr('Hide tools') : tr('Show tools')}
            </button>
          </div>

          <div className="flex flex-col gap-4 border-b border-slate-100 pb-4">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
              <div className="flex items-center gap-4">
                <div className="flex h-20 w-20 shrink-0 flex-col items-center justify-center rounded-2xl border border-slate-200 bg-slate-50 text-center">
                  <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">
                    {date.toLocaleDateString(locale, { month: 'short' })}
                  </span>
                  <span className="mt-1 text-3xl font-semibold text-ink">
                    {date.toLocaleDateString(locale, { day: '2-digit' })}
                  </span>
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-2 text-slate-400">
                    <CalendarDays size={16} />
                    <span className="text-xs font-semibold uppercase tracking-[0.18em]">{tr('Calendar')}</span>
                  </div>
                  <h2 className="mt-1 text-2xl font-semibold tracking-tight text-ink">{calendarTitle}</h2>
                  <p className="text-sm text-slate-500">{calendarSubtitle}</p>
                </div>
              </div>

              <div className="flex w-full flex-col gap-3 lg:max-w-[520px]">
                <div className="flex flex-col gap-3 xl:flex-row">
                  <div className="flex overflow-hidden rounded-xl border border-slate-200 bg-white xl:flex-1">
                    {(['day', 'week', 'month'] as ViewMode[]).map((v) => (
                      <button
                        key={v}
                        className={cn(
                          'flex-1 border-r border-slate-200 px-3 py-2 text-sm capitalize transition last:border-r-0',
                          view === v ? 'bg-ink text-white' : 'text-slate-600 hover:bg-slate-50',
                        )}
                        onClick={() => setView(v)}
                      >
                        {tr(v)}
                      </button>
                    ))}
                  </div>
                  <div className="inline-flex items-center gap-2 rounded-xl border border-slate-200 px-3 py-2.5 text-sm text-slate-500 xl:min-w-[190px]">
                    <Search size={15} className="text-slate-400" />
                    <select
                      value={filterStatus}
                      onChange={(e) => setFilterStatus(e.target.value as any)}
                      className="w-full bg-transparent text-sm text-slate-600 outline-none"
                    >
                      <option value="all">{tr('All statuses')}</option>
                      {(Object.keys(statusColors) as AppointmentStatus[]).map((s) => (
                        <option key={s} value={s}>
                          {tr(statusLabels[s])}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                  <div className="rounded-2xl bg-slate-50 px-4 py-3">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">{tr('Visible')}</p>
                    <p className="mt-1 text-2xl font-semibold text-ink">{filtered.length}</p>
                  </div>
                  <div className="rounded-2xl bg-slate-50 px-4 py-3">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">{tr('Doctors')}</p>
                    <p className="mt-1 text-2xl font-semibold text-ink">{visibleProviders.length || providerOptions.length}</p>
                  </div>
                  <div className="col-span-2 rounded-2xl bg-slate-50 px-4 py-3 sm:col-span-1">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">{tr('Selected day')}</p>
                    <p className="mt-1 text-2xl font-semibold text-ink">{selectedDateAppointments.length}</p>
                  </div>
                </div>
                {schedulingHealth && (
                  <div className="mt-3 rounded-2xl border border-lime-200 bg-lime-50 px-4 py-3">
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-lime-700">{tr('Schedule Health')}</p>
                        <p className="mt-1 text-xs text-slate-600">
                          {schedulingHealth.remindersDue} {tr('reminders due')} · {schedulingHealth.activeWaitlist} {tr('waitlist')} · {schedulingHealth.openOffers} {tr('offers')}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-semibold text-ink">{schedulingHealth.noShowRate}%</p>
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{tr('No-show')}</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <p className="text-sm text-slate-500">
                {displayMode === 'table'
                  ? view === 'day'
                    ? tr('Table view for the selected day. Click a row to edit the appointment.')
                    : view === 'week'
                      ? tr('Table view for the selected week. Click a row to edit the appointment.')
                      : tr('Table view for the selected month. Click a row to edit the appointment.')
                  : displayMode === 'resource'
                    ? tr('Resource view groups bookings by rooms, equipment, and unassigned appointments.')
                  : view === 'day'
                    ? tr('Drag blocks to reschedule • Hover for preview • Click empty space to create')
                    : view === 'week'
                      ? tr('Drag between days to reschedule')
                      : tr('Month overview for scheduled appointments')}
              </p>
              <div className="flex flex-wrap items-center gap-2 self-start md:self-auto">
                <div className="inline-flex overflow-hidden rounded-lg border border-slate-200 bg-white">
                  {(['calendar', 'resource', 'table'] as DisplayMode[]).map((mode) => (
                    <button
                      key={mode}
                      type="button"
                      onClick={() => setDisplayMode(mode)}
                      className={cn(
                        'inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium capitalize transition',
                        displayMode === mode ? 'bg-ink text-white' : 'text-slate-600 hover:bg-slate-50',
                      )}
                    >
                      {mode === 'calendar' ? <CalendarDays size={14} /> : <Rows3 size={14} />}
                      {tr(mode === 'resource' ? 'resources' : mode)}
                    </button>
                  ))}
                </div>
                {displayMode === 'table' ? (
                  <button
                    type="button"
                    onClick={() => setShowTableColumnDialog(true)}
                    aria-label={tr('Reorder table columns')}
                    title={tr('Reorder table columns')}
                    className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 transition hover:bg-slate-50"
                  >
                    <Rows3 size={15} />
                  </button>
                ) : null}
                <button
                  type="button"
                  onClick={() => openBlockoutDialogAt(date)}
                  aria-label={tr('Add blockout')}
                  title={tr('Add blockout')}
                  className="inline-flex h-9 items-center justify-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 text-xs font-semibold text-slate-600 transition hover:bg-slate-50"
                >
                  <Clock3 size={14} />
                  {tr('Blockout')}
                </button>
                <button
                  type="button"
                  onClick={() => setShowCalendarIntegrations(true)}
                  aria-label={tr('Calendar integrations')}
                  title={tr('Calendar integrations')}
                  className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 transition hover:bg-slate-50"
                >
                  <CalendarDays size={15} />
                </button>
                <button
                  type="button"
                  onClick={() => setShowRebookWaitlistDialog(true)}
                  aria-label={tr('Rebook and waitlist')}
                  title={tr('Rebook and waitlist')}
                  className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 transition hover:bg-slate-50"
                >
                  <UserPlus size={15} />
                </button>
                <label
                  className={cn(
                    'inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs md:self-auto',
                    displayMode === 'table' ? 'text-slate-400' : 'text-slate-600',
                  )}
                >
                  <span className={cn('font-medium', displayMode === 'table' ? 'text-slate-400' : 'text-slate-700')}>{tr('Hover preview')}</span>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={hoverPreviewEnabled}
                    aria-disabled={displayMode === 'table'}
                    disabled={displayMode === 'table'}
                    onClick={() => setHoverPreviewEnabled((current) => !current)}
                    className={cn(
                      'relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors disabled:cursor-not-allowed',
                      displayMode === 'table' ? 'bg-slate-200' : hoverPreviewEnabled ? 'bg-ink' : 'bg-slate-300',
                    )}
                  >
                    <span
                      className={cn(
                        'inline-block h-4 w-4 rounded-full bg-white transition-transform',
                        hoverPreviewEnabled ? 'translate-x-4' : 'translate-x-1',
                      )}
                    />
                  </button>
                </label>
              </div>
            </div>
          </div>

          <div className="appointments-scroll mt-4 min-h-0 flex-1 overflow-y-auto overflow-x-hidden">
            {error && <div className="mb-3 text-sm text-rose-600">{error}</div>}
            {loading && <div className="mb-3 text-sm text-slate-500">{tr('Loading appointments...')}</div>}
            {displayMode === 'table' ? (
              <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white">
                <div className="min-w-[1040px]">
                  <div
                    className="grid border-b border-slate-200 bg-slate-50 px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500"
                    style={{ gridTemplateColumns: appointmentTableGridTemplate }}
                  >
                    {appointmentTableColumnOrder.map((columnId) => (
                      <span key={columnId} className={columnId === 'actions' ? 'text-right' : ''}>
                        {appointmentTableColumnLabels[columnId]}
                      </span>
                    ))}
                  </div>
                  {!tableAppointments.length ? (
                    <div className="px-4 py-8 text-sm text-slate-500">{tr('No appointments in this range.')}</div>
                  ) : (
                    tableAppointments.map((appt) => {
                      const startDate = toDate(appt.start);
                      const endDate = toDate(appt.end);
                      const canOpenVisit = Boolean(appt.encounterId) || !['COMPLETED', 'CANCELLED', 'NO_SHOW'].includes(appt.status);
                      const canCheckIn = ['SCHEDULED', 'PORTAL_BOOKED'].includes(appt.status);
                      const canConfirm = !appt.confirmedAt && ['SCHEDULED', 'PORTAL_BOOKED'].includes(appt.status);
                      const canNoShow = !['COMPLETED', 'CANCELLED', 'NO_SHOW'].includes(appt.status);
                      const canCancel = !['COMPLETED', 'CANCELLED', 'NO_SHOW'].includes(appt.status);
                      const canRebook = ['COMPLETED', 'CANCELLED', 'NO_SHOW'].includes(appt.status);
                      const canManagePayment = !['CANCELLED', 'NO_SHOW'].includes(appt.status);
                      return (
                        <div
                          key={appt.id}
                          role="button"
                          tabIndex={0}
                          onClick={() => editAppointment(appt)}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter' || event.key === ' ') {
                              event.preventDefault();
                              editAppointment(appt);
                            }
                          }}
                          className="grid cursor-pointer items-center gap-3 border-b border-slate-100 px-4 py-3 text-sm transition hover:bg-slate-50 last:border-b-0"
                          style={{ gridTemplateColumns: appointmentTableGridTemplate }}
                        >
                          {(() => {
                            const serviceDisplay = getAppointmentServiceDisplay(appt);
                            const apptClassMeta = classMetaForAppointment(appt);
                            const columnContent: Record<AppointmentTableColumnId, JSX.Element> = {
                              time: (
                                <div className="min-w-0">
                            <p className="font-medium text-slate-900">
                              {startDate.toLocaleTimeString(locale, { hour: 'numeric', minute: '2-digit' })} - {endDate.toLocaleTimeString(locale, { hour: 'numeric', minute: '2-digit' })}
                            </p>
                            {view !== 'day' ? (
                              <p className="text-xs text-slate-500">{startDate.toLocaleDateString(locale, { weekday: 'short', month: 'short', day: 'numeric' })}</p>
                            ) : null}
                                </div>
                              ),
                              patient: (
                                <div className="min-w-0">
                            <p className="truncate font-medium text-slate-900">{appt.patient}</p>
                            <div className="mt-1 flex flex-wrap items-center gap-1.5 text-xs text-slate-500">
                              {appt.encounterId ? <span className="rounded-full bg-emerald-50 px-2 py-0.5 font-medium text-emerald-700">{tr('Visit open')}</span> : null}
                              {appt.isTelemed ? <span className="rounded-full bg-sky-50 px-2 py-0.5 font-medium text-sky-700">{tr('Telemed')}</span> : null}
                              {apptClassMeta.isClass ? <span className="rounded-full bg-emerald-50 px-2 py-0.5 font-medium text-emerald-700">{tr('Class')}</span> : null}
                            </div>
                                </div>
                              ),
                              provider: (
                                <div className="min-w-0">
                            <p className="truncate font-medium text-slate-800">{appt.provider}</p>
                            <p className="truncate text-xs text-slate-500">{appt.confirmedAt ? tr('Confirmed') : tr('Awaiting confirmation')}</p>
                                </div>
                              ),
                              service: (
                                <div className="min-w-0">
                            <p className="truncate font-medium text-slate-800">{tr(serviceDisplay.primaryName)}</p>
                            <p className="truncate text-xs text-slate-500">
                              {serviceDisplay.secondaryName ? `${tr(serviceDisplay.secondaryName)} · ` : ''}
                              {appt.location?.name || tr('No location')}
                            </p>
                                </div>
                              ),
                              status: (
                                <div>
                            <select
                              value={appt.status}
                              onClick={(event) => event.stopPropagation()}
                              onChange={(event) => void persistAppointmentUpdate(appt.id, { status: event.target.value })}
                              className={cn('max-w-full rounded-full border-0 px-2.5 py-1 text-xs font-medium outline-none', statusBadgeClasses[appt.status])}
                              aria-label={tr('Appointment status')}
                            >
                              {(Object.keys(statusLabels) as AppointmentStatus[]).map((status) => (
                                <option key={status} value={status}>{tr(statusLabels[status])}</option>
                              ))}
                            </select>
                                </div>
                              ),
                              actions: (
                                <div className="relative flex items-center justify-end gap-2" onClick={(event) => event.stopPropagation()}>
                            <button
                              type="button"
                              onClick={() => navigate(`/appointments/${appt.id}/payment`)}
                              disabled={!canManagePayment}
                              className="inline-flex h-9 items-center rounded-lg border border-slate-200 bg-white px-3 text-xs font-semibold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                            >
                              {tr(apptClassMeta.isClass ? 'Class payment' : 'Invoice')}
                            </button>
                            <button
                              type="button"
                              aria-haspopup="menu"
                              aria-expanded={tableActionMenuId === appt.id}
                              onClick={() => setTableActionMenuId((current) => (current === appt.id ? null : appt.id))}
                              className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 transition hover:bg-slate-50"
                            >
                              <MoreHorizontal size={16} />
                            </button>
                            {tableActionMenuId === appt.id ? (
                              <div className="absolute right-0 top-full z-20 mt-2 w-52 rounded-xl border border-slate-200 bg-white p-1 shadow-xl">
                                <button
                                  type="button"
                                  onClick={() => {
                                    setTableActionMenuId(null);
                                    editAppointment(appt);
                                  }}
                                  className="flex w-full items-center rounded-lg px-3 py-2 text-left text-sm text-slate-700 transition hover:bg-slate-50"
                                >
                                  {tr('Open appointment')}
                                </button>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setTableActionMenuId(null);
                                    if (canOpenVisit) void startOrOpenVisit(appt);
                                  }}
                                  disabled={!canOpenVisit || automationBusyId === `${appt.id}:visit`}
                                  className="flex w-full items-center rounded-lg px-3 py-2 text-left text-sm text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                                >
                                  {tr(appt.encounterId ? 'Open visit' : 'Start visit')}
                                </button>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setTableActionMenuId(null);
                                    if (canCheckIn) void runAppointmentAutomation(appt.id, 'check-in');
                                  }}
                                  disabled={!canCheckIn || automationBusyId === `${appt.id}:check-in`}
                                  className="flex w-full items-center rounded-lg px-3 py-2 text-left text-sm text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                                >
                                  {tr('Check in')}
                                </button>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setTableActionMenuId(null);
                                    if (canConfirm) void runAppointmentAutomation(appt.id, 'confirm');
                                  }}
                                  disabled={!canConfirm || automationBusyId === `${appt.id}:confirm`}
                                  className="flex w-full items-center rounded-lg px-3 py-2 text-left text-sm text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                                >
                                  {tr('Confirm')}
                                </button>
                                {appt.isTelemed ? (
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setTableActionMenuId(null);
                                      navigate(`/telemedicine/${appt.id}?role=host`);
                                    }}
                                    className="flex w-full items-center rounded-lg px-3 py-2 text-left text-sm text-slate-700 transition hover:bg-slate-50"
                                  >
                                    {tr('Open telemed room')}
                                  </button>
                                ) : null}
                                <button
                                  type="button"
                                  onClick={() => {
                                    setTableActionMenuId(null);
                                    navigate(`/appointments/${appt.id}/payment?focus=invoice`);
                                  }}
                                  className="flex w-full items-center rounded-lg px-3 py-2 text-left text-sm text-slate-700 transition hover:bg-slate-50"
                                >
                                  {tr(apptClassMeta.isClass ? 'Process class payment' : 'Invoice this appointment')}
                                </button>
                                {apptClassMeta.isClass ? (
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setTableActionMenuId(null);
                                      navigate(`/appointments/${appt.id}/payment?focus=class`);
                                    }}
                                    className="flex w-full items-center rounded-lg px-3 py-2 text-left text-sm text-emerald-700 transition hover:bg-emerald-50"
                                  >
                                    {tr('Class Payments')}
                                  </button>
                                ) : null}
                                <button
                                  type="button"
                                  onClick={() => {
                                    setTableActionMenuId(null);
                                    navigate(`/appointments/${appt.id}/payment?focus=deposit`);
                                  }}
                                  className="flex w-full items-center rounded-lg px-3 py-2 text-left text-sm text-slate-700 transition hover:bg-slate-50"
                                >
                                  {tr('Booking deposit')}
                                </button>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setTableActionMenuId(null);
                                    navigate(`/appointments/${appt.id}/payment?focus=hold`);
                                  }}
                                  className="flex w-full items-center rounded-lg px-3 py-2 text-left text-sm text-slate-700 transition hover:bg-slate-50"
                                >
                                  {tr('Payment hold')}
                                </button>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setTableActionMenuId(null);
                                    navigate(`/appointments/${appt.id}/payment?focus=partial`);
                                  }}
                                  className="flex w-full items-center rounded-lg px-3 py-2 text-left text-sm text-slate-700 transition hover:bg-slate-50"
                                >
                                  {tr('Part-paid invoice')}
                                </button>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setTableActionMenuId(null);
                                    void createAppointmentLetter(appt);
                                  }}
                                  disabled={saving}
                                  className="flex w-full items-center rounded-lg px-3 py-2 text-left text-sm text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                                >
                                  {tr('Create appointment letter')}
                                </button>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setTableActionMenuId(null);
                                    void openVideoAiNotes(appt);
                                  }}
                                  disabled={!appt.isTelemed && !appt.encounterId}
                                  className="flex w-full items-center rounded-lg px-3 py-2 text-left text-sm text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                                >
                                  {tr('Video AI notes')}
                                </button>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setTableActionMenuId(null);
                                    if (canRebook) void rebookAppointment(appt);
                                  }}
                                  disabled={!canRebook || saving}
                                  className="flex w-full items-center rounded-lg px-3 py-2 text-left text-sm text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                                >
                                  {tr('Rebook appointment')}
                                </button>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setTableActionMenuId(null);
                                    if (canNoShow) void runAppointmentAutomation(appt.id, 'no-show');
                                  }}
                                  disabled={!canNoShow || automationBusyId === `${appt.id}:no-show`}
                                  className="flex w-full items-center rounded-lg px-3 py-2 text-left text-sm text-amber-700 transition hover:bg-amber-50 disabled:cursor-not-allowed disabled:opacity-50"
                                >
                                  {tr('No-show')}
                                </button>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setTableActionMenuId(null);
                                    if (canCancel) void persistAppointmentUpdate(appt.id, { status: 'CANCELLED' });
                                  }}
                                  disabled={!canCancel || saving}
                                  className="flex w-full items-center rounded-lg px-3 py-2 text-left text-sm text-rose-700 transition hover:bg-rose-50 disabled:cursor-not-allowed disabled:opacity-50"
                                >
                                  {tr('Cancel appointment')}
                                </button>
                              </div>
                            ) : null}
                                </div>
                              ),
                            };
                            return appointmentTableColumnOrder.map((columnId) => (
                              <div key={`${appt.id}-${columnId}`} className="min-w-0">
                                {columnContent[columnId]}
                              </div>
                            ));
                        })()}
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            ) : displayMode === 'resource' ? ResourceGrid() : view === 'month' ? MonthGrid() : view === 'week' ? WeekGrid() : DayGrid()}
          </div>
        </section>
      </div>



      {showBlockoutDialog ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4">
          <div className="w-full max-w-xl rounded-2xl bg-white shadow-2xl">
            <div className="flex items-start justify-between gap-4 border-b border-slate-100 px-5 py-4">
              <div>
                <h3 className="text-lg font-semibold text-ink">{pendingBlockout.id ? tr('Edit Blockout') : tr('Time Off Blockout')}</h3>
                <p className="mt-1 text-sm text-slate-500">{tr('Block unavailable time for one or more staff members.')}</p>
              </div>
              <button
                type="button"
                onClick={() => setShowBlockoutDialog(false)}
                aria-label={tr('Close')}
                className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 text-slate-500 transition hover:bg-slate-50"
              >
                <X size={16} />
              </button>
            </div>
            <div className="space-y-4 px-5 py-4">
              <div className="grid gap-3 sm:grid-cols-2">
                <label className="space-y-1 text-xs font-semibold text-slate-600">
                  {tr('Title')}
                  <input className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" value={pendingBlockout.title} onChange={(event) => setPendingBlockout((prev) => ({ ...prev, title: event.target.value }))} />
                </label>
                <label className="space-y-1 text-xs font-semibold text-slate-600">
                  {tr('Start')}
                  <input className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" type="datetime-local" value={pendingBlockout.start} onChange={(event) => setPendingBlockout((prev) => ({ ...prev, start: event.target.value }))} />
                </label>
                <label className="space-y-1 text-xs font-semibold text-slate-600">
                  {tr('Duration (minutes)')}
                  <input className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" type="number" min={5} value={pendingBlockout.durationMinutes} onChange={(event) => setPendingBlockout((prev) => ({ ...prev, durationMinutes: Number(event.target.value) || 60 }))} />
                </label>
                <label className="space-y-1 text-xs font-semibold text-slate-600">
                  {tr('Reason')}
                  <input className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" value={pendingBlockout.reason} onChange={(event) => setPendingBlockout((prev) => ({ ...prev, reason: event.target.value }))} />
                </label>
              </div>
              <div className="space-y-2">
                <p className="text-xs font-semibold text-slate-600">{tr('Multi-Staff Blockouts')}</p>
                <div className="flex flex-wrap gap-2 rounded-lg border border-slate-200 bg-slate-50 p-3">
                  {providerOptions.map((provider) => {
                    const selected = pendingBlockout.providerIds.includes(provider.id);
                    return (
                      <button
                        key={`blockout-provider-${provider.id}`}
                        type="button"
                        onClick={() => setPendingBlockout((prev) => ({
                          ...prev,
                          providerIds: selected ? prev.providerIds.filter((id) => id !== provider.id) : [...prev.providerIds, provider.id],
                        }))}
                        className={cn('rounded-full border px-3 py-1.5 text-xs font-semibold transition', selected ? 'border-slate-900 bg-slate-900 text-white' : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50')}
                      >
                        {provider.name}
                      </button>
                    );
                  })}
                  {!providerOptions.length ? <span className="text-xs text-slate-500">{tr('No staff available')}</span> : null}
                </div>
              </div>
              {!pendingBlockout.id ? (
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                  <label className="inline-flex items-center gap-2 text-sm font-semibold text-slate-700">
                    <input type="checkbox" checked={pendingBlockout.repeatEnabled} onChange={(event) => setPendingBlockout((prev) => ({ ...prev, repeatEnabled: event.target.checked }))} />
                    {tr('Repeating Blockouts')}
                  </label>
                  {pendingBlockout.repeatEnabled ? (
                    <div className="mt-3 grid gap-3 sm:grid-cols-2">
                      <label className="space-y-1 text-xs font-semibold text-slate-600">
                        {tr('Frequency')}
                        <select className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm" value={pendingBlockout.repeatFrequency} onChange={(event) => setPendingBlockout((prev) => ({ ...prev, repeatFrequency: event.target.value as 'daily' | 'weekly' | 'monthly' }))}>
                          <option value="daily">{tr('Daily')}</option>
                          <option value="weekly">{tr('Weekly')}</option>
                          <option value="monthly">{tr('Monthly')}</option>
                        </select>
                      </label>
                      <label className="space-y-1 text-xs font-semibold text-slate-600">
                        {tr('Occurrences')}
                        <input className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm" type="number" min={1} max={52} value={pendingBlockout.repeatCount} onChange={(event) => setPendingBlockout((prev) => ({ ...prev, repeatCount: Number(event.target.value) || 1 }))} />
                      </label>
                    </div>
                  ) : null}
                </div>
              ) : null}
            </div>
            <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-100 px-5 py-4">
              <div className="flex flex-wrap gap-2">
                {pendingBlockout.id ? (
                  <>
                    <button
                      type="button"
                      onClick={() => void deleteBlockout(pendingBlockout.id as string, 'single')}
                      className="rounded-lg border border-rose-200 px-3 py-2 text-sm font-semibold text-rose-700 hover:bg-rose-50"
                    >
                      {tr('Delete Blockout')}
                    </button>
                    {pendingBlockout.seriesId ? (
                      <button
                        type="button"
                        onClick={() => void deleteBlockout(pendingBlockout.id as string, 'series')}
                        className="rounded-lg border border-rose-200 px-3 py-2 text-sm font-semibold text-rose-700 hover:bg-rose-50"
                      >
                        {tr('Delete Series')}
                      </button>
                    ) : null}
                  </>
                ) : null}
              </div>
              <div className="flex gap-2">
                <button type="button" onClick={() => setShowBlockoutDialog(false)} className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">{tr('Cancel')}</button>
                <button type="button" onClick={() => void saveBlockout()} className="rounded-lg bg-ink px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800">{tr('Save Blockout')}</button>
              </div>
            </div>
          </div>
        </div>
      ) : null}



      {showTableColumnDialog ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4">
          <div className="w-full max-w-md rounded-2xl bg-white shadow-2xl">
            <div className="flex items-start justify-between gap-4 border-b border-slate-100 px-5 py-4">
              <div>
                <h3 className="text-lg font-semibold text-ink">{tr('Reorder table columns')}</h3>
                <p className="mt-1 text-sm text-slate-500">{tr('Choose the order used by the calendar table view.')}</p>
              </div>
              <button
                type="button"
                onClick={() => setShowTableColumnDialog(false)}
                aria-label={tr('Close')}
                className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 text-slate-500 transition hover:bg-slate-50"
              >
                <X size={16} />
              </button>
            </div>
            <div className="space-y-2 px-5 py-4">
              {appointmentTableColumnOrder.map((columnId, index) => (
                <div key={columnId} className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2">
                  <div className="flex items-center gap-2">
                    <Rows3 size={14} className="text-slate-400" />
                    <span className="text-sm font-medium text-slate-800">{appointmentTableColumnLabels[columnId]}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => moveAppointmentTableColumn(columnId, -1)}
                      disabled={index === 0}
                      className="rounded-md border border-slate-200 bg-white px-2 py-1 text-xs font-medium text-slate-600 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
                    >
                      {tr('Up')}
                    </button>
                    <button
                      type="button"
                      onClick={() => moveAppointmentTableColumn(columnId, 1)}
                      disabled={index === appointmentTableColumnOrder.length - 1}
                      className="rounded-md border border-slate-200 bg-white px-2 py-1 text-xs font-medium text-slate-600 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
                    >
                      {tr('Down')}
                    </button>
                  </div>
                </div>
              ))}
            </div>
            <div className="flex items-center justify-between gap-3 border-t border-slate-100 px-5 py-4">
              <button
                type="button"
                onClick={() => {
                  setAppointmentTableColumnOrder(defaultAppointmentTableColumnOrder);
                  void saveSchedulingPatch({ calendarColumnOrder: { providerIds: providerOptions.map((provider) => provider.id), tableColumns: defaultAppointmentTableColumnOrder } });
                }}
                className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50"
              >
                {tr('Reset')}
              </button>
              <button
                type="button"
                onClick={() => setShowTableColumnDialog(false)}
                className="rounded-lg bg-ink px-4 py-2 text-sm font-semibold text-white transition hover:bg-slate-800"
              >
                {tr('Done')}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {showCalendarIntegrations ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4">
          <div className="w-full max-w-md rounded-2xl bg-white shadow-2xl">
            <div className="flex items-start justify-between gap-4 border-b border-slate-100 px-5 py-4">
              <div>
                <h3 className="text-lg font-semibold text-ink">{tr('Calendar integrations')}</h3>
                <p className="mt-1 text-sm text-slate-500">{tr('Connect Google, Outlook, or an external calendar feed.')}</p>
              </div>
              <button
                type="button"
                onClick={() => setShowCalendarIntegrations(false)}
                aria-label={tr('Close')}
                className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 text-slate-500 transition hover:bg-slate-50"
              >
                <X size={16} />
              </button>
            </div>
            <div className="space-y-2 px-5 py-4">
              {(externalCalendarSync.length ? externalCalendarSync : [
                { id: 'sync-google', provider: 'Google Calendar', enabled: false, mode: 'two-way' as const, accountLabel: '' },
                { id: 'sync-outlook', provider: 'Outlook Calendar', enabled: false, mode: 'two-way' as const, accountLabel: '' },
                { id: 'sync-ics', provider: 'ICS feed', enabled: false, mode: 'one-way' as const, accountLabel: '' },
              ]).map((integration) => (
                <div key={integration.id} className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 bg-slate-50 px-3 py-3">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-slate-800">{tr(integration.provider)}</p>
                    <p className="mt-0.5 text-xs text-slate-500">
                      {integration.mode === 'two-way' ? tr('Two-way sync for provider availability and appointments.') : tr('Read-only external calendar feed for personal calendars.')}
                    </p>
                    {integration.lastSyncedAt ? <p className="mt-1 text-[11px] text-slate-400">{tr('Last synced')}: {new Date(integration.lastSyncedAt).toLocaleString(locale)}</p> : null}
                  </div>
                  <button
                    type="button"
                    onClick={() => void updateExternalSync(integration.id, { enabled: !integration.enabled })}
                    className={cn('shrink-0 rounded-md border px-3 py-1.5 text-xs font-semibold transition', integration.enabled ? 'border-emerald-200 bg-emerald-50 text-emerald-700' : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50')}
                  >
                    {integration.enabled ? tr('Connected') : tr('Connect')}
                  </button>
                </div>
              ))}
            </div>
            <div className="flex justify-end border-t border-slate-100 px-5 py-4">
              <button
                type="button"
                onClick={() => setShowCalendarIntegrations(false)}
                className="rounded-lg bg-ink px-4 py-2 text-sm font-semibold text-white transition hover:bg-slate-800"
              >
                {tr('Done')}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {showRebookWaitlistDialog ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4">
          <div className="w-full max-w-md rounded-2xl bg-white shadow-2xl">
            <div className="flex items-start justify-between gap-4 border-b border-slate-100 px-5 py-4">
              <div>
                <h3 className="text-lg font-semibold text-ink">{tr('Rebook and waitlist')}</h3>
                <p className="mt-1 text-sm text-slate-500">{tr('Audit notes, waitlist hooks, and room rules')}</p>
              </div>
              <button
                type="button"
                onClick={() => setShowRebookWaitlistDialog(false)}
                aria-label={tr('Close')}
                className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 text-slate-500 transition hover:bg-slate-50"
              >
                <X size={16} />
              </button>
            </div>
            <div className="space-y-3 px-5 py-4">
              <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500">
                {tr('Audit note')}
                <textarea
                  value={rescheduleAuditNote}
                  onChange={(event) => setRescheduleAuditNote(event.target.value)}
                  rows={4}
                  placeholder={tr('Reschedule or rebook audit note')}
                  className="mt-1 w-full resize-none rounded-lg border border-slate-200 px-3 py-2 text-sm font-normal normal-case tracking-normal text-slate-700 outline-none focus:border-sky-300"
                />
              </label>
              <label className="flex items-start gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-600">
                <input type="checkbox" className="mt-1" checked={waitlistNotifications} onChange={(event) => setWaitlistNotifications(event.target.checked)} />
                <span>
                  <span className="block font-semibold text-slate-700">{tr('Waitlist notifications')}</span>
                  <span className="block text-xs text-slate-500">{tr('Notify matching waitlist clients when a slot opens.')}</span>
                </span>
              </label>
              <label className="flex items-start gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-600">
                <input type="checkbox" className="mt-1" checked={resourceRulesEnabled} onChange={(event) => setResourceRulesEnabled(event.target.checked)} />
                <span>
                  <span className="block font-semibold text-slate-700">{tr('Room and resource rules')}</span>
                  <span className="block text-xs text-slate-500">{tr('Warn before changing restricted rooms or unavailable resources.')}</span>
                </span>
              </label>
            </div>
            <div className="flex justify-end border-t border-slate-100 px-5 py-4">
              <button
                type="button"
                onClick={() => setShowRebookWaitlistDialog(false)}
                className="rounded-lg bg-ink px-4 py-2 text-sm font-semibold text-white transition hover:bg-slate-800"
              >
                {tr('Done')}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {hovered && (

        <div
          className="fixed z-50 w-[320px] max-w-[calc(100vw-32px)] rounded-2xl border border-slate-200 bg-white p-4 shadow-2xl"
          style={(() => {
            const previewWidth = 320;
            const previewHeight = 330;
            const margin = 16;
            const anchorRect = hovered.anchorRect;
            const viewportWidth = typeof window !== 'undefined' ? window.innerWidth : previewWidth + margin * 2;
            const viewportHeight = typeof window !== 'undefined' ? window.innerHeight : 900;
            const left = Math.min(
              Math.max(anchorRect.left, margin),
              Math.max(margin, viewportWidth - previewWidth - margin),
            );
            const prefersAbove = anchorRect.top > 280;
            const top = prefersAbove
              ? Math.max(margin, anchorRect.top - 12)
              : Math.min(viewportHeight - previewHeight - margin, anchorRect.bottom + 12);

            return prefersAbove
              ? { left, top, transform: 'translateY(-100%)' }
              : { left, top };
          })()}
          onMouseEnter={clearHoverHideTimeout}
          onMouseLeave={scheduleHoverPreviewClose}
        >

          <p className="text-xs uppercase tracking-wide text-slate-400">{tr('Preview')}</p>

          <h4 className="font-semibold text-ink">{hovered.appointment.patient}</h4>

          <p className="text-sm text-slate-600">{tr(getAppointmentServiceDisplay(hovered.appointment).primaryName)}</p>

          {getAppointmentServiceDisplay(hovered.appointment).secondaryName ? (
            <p className="text-xs text-slate-500">{tr('Appointment type')}: {tr(getAppointmentServiceDisplay(hovered.appointment).secondaryName)}</p>
          ) : null}

          {hovered.appointment.title && <p className="text-sm text-slate-500">{tr(hovered.appointment.title)}</p>}

          <p className="text-sm text-slate-500 flex items-center gap-2">

            <Clock3 size={14} />{' '}

            {toDate(hovered.appointment.start).toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' })}{' '}

            - {toDate(hovered.appointment.end).toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' })}

          </p>

          <p className="text-sm text-slate-500 flex items-center gap-2">

          </p>

          <span className={cn('pill mt-2', statusColors[hovered.appointment.status])}>{tr(statusLabels[hovered.appointment.status])}</span>
          <div className="mt-3 grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => startOrOpenVisit(hovered.appointment)}
              disabled={automationBusyId === `${hovered.appointment.id}:visit` || ['COMPLETED', 'CANCELLED', 'NO_SHOW'].includes(hovered.appointment.status)}
              className="rounded-lg border border-emerald-200 bg-emerald-50 px-2 py-1.5 text-[11px] font-semibold text-emerald-700 disabled:opacity-60"
            >
              <span className="inline-flex items-center justify-center gap-1.5">
                <Stethoscope size={12} />
                {tr(hovered.appointment.encounterId ? 'Open visit' : 'Start visit')}
              </span>
            </button>
            <button
              type="button"
              onClick={() => runAppointmentAutomation(hovered.appointment.id, 'schedule-reminders')}
              disabled={automationBusyId === `${hovered.appointment.id}:schedule-reminders`}
              className="rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-[11px] font-semibold text-slate-700 disabled:opacity-60"
            >
              {tr('Reminders')}
            </button>
            <button
              type="button"
              onClick={() => runAppointmentAutomation(hovered.appointment.id, 'care-tasks')}
              disabled={automationBusyId === `${hovered.appointment.id}:care-tasks`}
              className="rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-[11px] font-semibold text-slate-700 disabled:opacity-60"
            >
              {tr('Care tasks')}
            </button>
            <button
              type="button"
              onClick={() => runAppointmentAutomation(hovered.appointment.id, 'check-in')}
              disabled={automationBusyId === `${hovered.appointment.id}:check-in`}
              className="rounded-lg border border-emerald-200 bg-emerald-50 px-2 py-1.5 text-[11px] font-semibold text-emerald-700 disabled:opacity-60"
            >
              {tr('Check in')}
            </button>
            <button
              type="button"
              onClick={() => runAppointmentAutomation(hovered.appointment.id, 'no-show')}
              disabled={automationBusyId === `${hovered.appointment.id}:no-show`}
              className="rounded-lg border border-amber-200 bg-amber-50 px-2 py-1.5 text-[11px] font-semibold text-amber-700 disabled:opacity-60"
            >
              {tr('No-show')}
            </button>
          </div>
          {(hovered.appointment.reminderSummary || hovered.appointment.careTaskSummary) && (
            <p className="mt-2 text-[11px] text-slate-500">
              {tr('Pending')}: {hovered.appointment.reminderSummary?.pending ?? 0} {tr('reminders')}, {hovered.appointment.careTaskSummary?.pending ?? 0} {tr('tasks')}
            </p>
          )}

        </div>

      )}


      {showBookingWizard ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/40 p-4">
          <div className="max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-2xl bg-white shadow-2xl">
            <div className="flex items-start justify-between border-b border-slate-200 px-6 py-5">
              <div>
                <h2 className="text-xl font-semibold text-ink">{tr('Booking wizard')}</h2>
                <p className="mt-1 text-sm text-slate-500">{tr('Build a multi-service appointment, quick-create a client, or book from a package.')}</p>
              </div>
              <button type="button" onClick={() => setShowBookingWizard(false)} className="rounded-lg p-1 text-slate-500 hover:bg-slate-100" aria-label={tr('Close')}>
                <X size={20} />
              </button>
            </div>
            <div className="space-y-5 px-6 py-5">
              <section className="rounded-xl border border-slate-200 p-4">
                <h3 className="font-semibold text-ink">{tr('Client')}</h3>
                <div className="mt-3 grid gap-2 sm:grid-cols-2">
                  <button type="button" onClick={() => setWizardClientMode('existing')} className={cn('rounded-lg border px-3 py-2 text-left text-sm', wizardClientMode === 'existing' ? 'border-sky-300 bg-sky-50 text-sky-700' : 'border-slate-200 text-slate-700')}>
                    {tr('Use existing client')}
                  </button>
                  <button type="button" onClick={() => setWizardClientMode('quick-create')} className={cn('rounded-lg border px-3 py-2 text-left text-sm', wizardClientMode === 'quick-create' ? 'border-sky-300 bg-sky-50 text-sky-700' : 'border-slate-200 text-slate-700')}>
                    {tr('Quick create client')}
                  </button>
                </div>
                {wizardClientMode === 'quick-create' ? (
                  <div className="mt-3 grid gap-3 sm:grid-cols-3">
                    <input className="rounded-lg border border-slate-200 px-3 py-2 text-sm" placeholder={tr('First name')} />
                    <input className="rounded-lg border border-slate-200 px-3 py-2 text-sm" placeholder={tr('Last name')} />
                    <input className="rounded-lg border border-slate-200 px-3 py-2 text-sm" placeholder={tr('Phone or email')} />
                  </div>
                ) : null}
              </section>
              <section className="rounded-xl border border-slate-200 p-4">
                <div className="flex items-center justify-between gap-3">
                  <h3 className="font-semibold text-ink">{tr('Services')}</h3>
                  <label className="inline-flex items-center gap-2 text-sm text-slate-600">
                    <input type="checkbox" checked={wizardPackageMode} onChange={(event) => setWizardPackageMode(event.target.checked)} />
                    {tr('Book from package')}
                  </label>
                </div>
                <div className="mt-3 grid gap-2 md:grid-cols-2">
                  {appointmentTypes.slice(0, 8).map((type) => {
                    const active = wizardSelectedServices.includes(type.id);
                    return (
                      <button
                        key={type.id}
                        type="button"
                        onClick={() => setWizardSelectedServices((current) => (active ? current.filter((id) => id !== type.id) : [...current, type.id]))}
                        className={cn('rounded-lg border px-3 py-2 text-left text-sm', active ? 'border-sky-300 bg-sky-50 text-sky-700' : 'border-slate-200 text-slate-700 hover:bg-slate-50')}
                      >
                        <span className="block font-semibold">{type.name}</span>
                        <span className="text-xs text-slate-500">{type.durationMinutes} {tr('minutes')}</span>
                      </button>
                    );
                  })}
                  {!appointmentTypes.length ? <p className="text-sm text-slate-500">{tr('No services configured yet.')}</p> : null}
                </div>
              </section>
              <section className="rounded-xl border border-slate-200 p-4">
                <h3 className="font-semibold text-ink">{tr('Booking review')}</h3>
                <div className="mt-3 grid gap-3 text-sm sm:grid-cols-3">
                  <div className="rounded-lg bg-slate-50 p-3"><p className="text-slate-500">{tr('Services')}</p><p className="font-semibold text-ink">{wizardSelectedServices.length || 1}</p></div>
                  <div className="rounded-lg bg-slate-50 p-3"><p className="text-slate-500">{tr('Mode')}</p><p className="font-semibold text-ink">{wizardPackageMode ? tr('Package') : tr('Standard')}</p></div>
                  <div className="rounded-lg bg-slate-50 p-3"><p className="text-slate-500">{tr('Client')}</p><p className="font-semibold text-ink">{wizardClientMode === 'quick-create' ? tr('Quick create') : tr('Existing')}</p></div>
                </div>
              </section>
            </div>
            <div className="flex justify-end gap-2 border-t border-slate-200 px-6 py-4">
              <button type="button" onClick={() => setShowBookingWizard(false)} className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">{tr('Cancel')}</button>
              <button type="button" onClick={() => { setShowBookingWizard(false); openModalAt(date); }} className="rounded-lg bg-sky-600 px-4 py-2 text-sm font-semibold text-white hover:bg-sky-700">{tr('Continue booking')}</button>
            </div>
          </div>
        </div>
      ) : null}



      <AppointmentModal

        isOpen={modalOpen}

        onClose={() => setModalOpen(false)}

        onSubmit={submitNewAppointment}

        onAppointmentActionComplete={async () => {
          await Promise.all([loadAppointments(), loadSchedulingHealth()]);
        }}

        initialData={pending}

        providerOptions={providerOptions}

        patientOptions={patientOptions}

        appointmentTypes={appointmentTypes}
        locationOptions={locationOptions}
        roomOptions={roomOptions}
        assetOptions={assetOptions}
        medicalFormOptions={medicalFormOptions}
        serviceVariantsEnabled={serviceVariantsEnabled}

        defaultSlotMinutes={defaultSlotMinutes}

        getToken={getToken}

      />



    </div>

  );

}

function LegendRow({ color, label }: { color: string; label: string }) {
  return (
    <div className="flex items-center gap-2">
      <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: color }} />
      <span>{label}</span>
    </div>
  );
}



type BlockProps = {

  appt: Appointment;
  patientAvatarUrl?: string | null;

  onDragStart: (e: React.DragEvent, appt: Appointment) => void;

  onHover: (appt: Appointment, anchorRect: DOMRect) => void;

  onLeave: () => void;

  onClick: () => void;

  compact?: boolean;

  staticLayout?: boolean;
  allowClientCardAccess?: boolean;

};



function ApptBlock({

  appt,
  patientAvatarUrl,

  color,

  onDragStart,

  onHover,

  onLeave,

  onClick,

  compact,

  staticLayout,

  heightPx,
  allowClientCardAccess = true,

}: BlockProps & { color?: string; heightPx?: number }) {
  const { tr, language } = useI18n();
  const navigate = useNavigate();
  const locale = language === 'es' ? 'es-ES' : language === 'it' ? 'it-IT' : 'en-US';
  const serviceDisplay = getAppointmentServiceDisplay(appt);

  const start = toDate(appt.start);

  const end = toDate(appt.end);
  const resolvedPatientAvatarUrl = patientAvatarUrl ?? appt.patientAvatarUrl ?? null;
  const patientInitials = appt.patient
    .split(' ')
    .filter(Boolean)
    .map((part) => part[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

  const isTelemed = Boolean(appt.isTelemed || appt.appointmentType.isTelemed);
  const canManagePayment = !['CANCELLED', 'NO_SHOW'].includes(appt.status);

  const dynamicSize = heightPx ? { height: `${heightPx}px`, minHeight: `${heightPx}px` } : {};



  const copyLink = async (e: React.MouseEvent) => {

    e.stopPropagation();

    if (!appt.joinUrl) return;

    try {

      await navigator.clipboard.writeText(appt.joinUrl);

    } catch {

      // ignore clipboard failures

    }

  };



  const joinLink = (e: React.MouseEvent) => {

    e.stopPropagation();

    if (appt.joinUrl) window.open(appt.joinUrl, '_blank', 'noopener,noreferrer');

  };

  return (

    <div

      draggable

      onDragStart={(e) => onDragStart(e, appt)}

      onMouseEnter={(event) => onHover(appt, event.currentTarget.getBoundingClientRect())}

      onMouseLeave={onLeave}

      onClick={(e) => {

        e.stopPropagation();

        onClick();

      }}

      className={cn(

        'rounded-l-sm rounded-r-lg text-xs text-ink shadow-sm bg-white flex flex-col gap-1 leading-tight overflow-hidden border-t border-b border-r border-slate-200 hover:shadow-md hover:bg-slate-50 transition-all z-10',

        staticLayout ? 'relative w-full min-h-[76px] p-3' : 'absolute inset-1 p-3 min-h-[76px]',

        compact && 'text-[11px] p-1.5 min-h-[60px]',

        'cursor-move',

      )}

      style={{ borderLeft: `4px solid ${color || '#3b82f6'}`, ...dynamicSize }}

    >

      <div className="flex items-center justify-between gap-2">

        <div className="flex min-w-0 items-center gap-2">
          {resolvedPatientAvatarUrl ? (
            <img src={resolvedPatientAvatarUrl} alt={appt.patient} className="h-5 w-5 rounded-full border border-slate-200 object-cover shrink-0" />
          ) : (
            <span className="h-5 w-5 rounded-full bg-slate-100 text-slate-600 text-[9px] font-semibold flex items-center justify-center shrink-0">
              {patientInitials || 'PT'}
            </span>
          )}
          <span className="font-semibold text-slate-700 truncate">{appt.patient}</span>
        </div>

        <span

          className={cn(

            'inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide whitespace-nowrap',

            statusBadgeClasses[appt.status],

          )}

        >

          {tr(statusLabels[appt.status])}

        </span>

      </div>



      <div className="flex items-center gap-1 text-[10px] text-slate-500 font-medium">

        <span className="uppercase tracking-wider opacity-75">{tr(serviceDisplay.primaryName)}</span>
        {serviceDisplay.secondaryName ? <span className="truncate text-[9px] normal-case text-slate-400">{tr(serviceDisplay.secondaryName)}</span> : null}
        <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[9px] font-semibold text-slate-600">
          {appt.source === 'PORTAL' ? tr('Portal') : tr('Staff')}
        </span>

      </div>
      {appt.roomName ? (
        <div className="text-[10px] text-slate-500">
          {tr('Room')}: {appt.roomName}
        </div>
      ) : null}



      {isTelemed && (

        <div className="flex items-center gap-2 text-[10px] text-slate-600">

          <Clock3 size={10} />

          {start.toLocaleTimeString(locale, { hour: 'numeric', minute: '2-digit' })} -{' '}

          {end.toLocaleTimeString(locale, { hour: 'numeric', minute: '2-digit' })}

          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-sky-50 text-sky-700 border border-sky-100">

            <Video size={10} /> {tr('Telemed')}

          </span>

          {appt.joinUrl ? (

            <>

              <span onClick={joinLink} title={tr('Join session')} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-sky-50 text-sky-700 border border-sky-100">

                {tr('Join')}

              </span>

            </>

          ) : (

            <></>

          )}



        </div>

      )}



      <div className="mt-auto flex items-center justify-between gap-2">

        <div className="text-slate-500 flex items-center gap-1 text-[10px] min-w-0">

          <Clock3 size={10} />

          <span className="truncate">
            {start.toLocaleTimeString(locale, { hour: 'numeric', minute: '2-digit' })} -{' '}
            {end.toLocaleTimeString(locale, { hour: 'numeric', minute: '2-digit' })}
          </span>

        </div>

        {allowClientCardAccess ? <button
          type="button"
          draggable={false}
          onClick={(event) => {
            event.stopPropagation();
            navigate(`/patients/${appt.patientId}`);
          }}
          className="inline-flex shrink-0 items-center rounded-full border border-slate-200 bg-white px-2.5 py-1 text-[10px] font-semibold text-slate-700 transition hover:bg-slate-50"
        >
          {tr('Client card')}
        </button> : null}

        <button
          type="button"
          draggable={false}
          onClick={(event) => {
            event.stopPropagation();
            navigate(`/appointments/${appt.id}/payment`);
          }}
          disabled={!canManagePayment}
          className="inline-flex shrink-0 items-center rounded-full border border-slate-200 bg-white px-2.5 py-1 text-[10px] font-semibold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {tr('Payment')}
        </button>

      </div>

    </div>

  );

}
